import { Component } from "@theme/component";
import { getPreferredFreeGiftSelection } from "@theme/free-gift-preferences";
import {
  getRechargeDynamicBundleCartItems,
  getRechargeBundleGroupIds,
  getRechargeBundleLineKeysToRemove,
  normalizeSellingPlanId,
  serializeRechargeBundleCartMutation,
} from "@theme/recharge-dynamic-bundle";
import {
  fetchConfig,
  getVoucherDiscountCodesForHandle,
  preloadImage,
  onAnimationEnd,
  persistVoucherCartAttribute,
  yieldToMainThread,
} from "@theme/utilities";
import {
  DiscountUpdateEvent,
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const AFFINITY_ADD_TO_BOX_LABEL = "+ Add to Box";
const AFFINITY_RECHARGE_PORTAL_URL_FALLBACK =
  "https://account.goodchop.com/pages/8cfdfe48-f906-45d6-8515-29818e34a6d4";
const AFFINITY_PORTAL_AUTO_OPEN_BUNDLE_PARAM = "affinity_open_bundle_editor";
const AFFINITY_PORTAL_AUTO_OPEN_BUNDLE_STORAGE_KEY =
  "affinity:auto-open-bundle-editor";
const AFFINITY_PORTAL_OPEN_SUBSCRIPTIONS_STORAGE_KEY =
  "affinity:auto-open-subscriptions";
const AFFINITY_RECHARGE_READY_TIMEOUT = 4000;
const AFFINITY_RECHARGE_READY_INTERVAL = 100;

let cachedHasRechargeSubscriptions = null;
let rechargeSubscriptionPresenceRequest = null;
let affinityRechargeSession = null;
let affinityRechargeSessionRequest = null;
let affinitySubscriptionBootstrapRequest = null;
let cachedAffinityCustomerLoginResolved = false;
let cachedAffinityCustomerLoggedIn = false;
let lastAffinitySubscriptionPresenceResolvedAt = 0;
const AFFINITY_CUSTOMER_LOGIN_HINT_KEY = "affinity:customer-login-hint";
const AFFINITY_CUSTOMER_LOGIN_PENDING_KEY = "affinity:customer-login-pending";
const AFFINITY_PORTAL_REDIRECT_AFTER_LOGIN_KEY =
  "affinity:portal-redirect-after-login";
const AFFINITY_CUSTOMER_LOGIN_HINT_TTL_MS = 30 * 60 * 1000;
const AFFINITY_SUBSCRIPTION_PRESENCE_CACHE_TTL_MS = 10 * 60 * 1000;
const AFFINITY_SUBSCRIPTION_PRESENCE_PENDING_RECHECK_MS = 2 * 60 * 1000;
const AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_KEY =
  "affinity:subscription-checkout-pending";
const AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_TTL_MS = 2 * 60 * 60 * 1000;
const AFFINITY_RECHARGE_READY_EVENT = "affinity:recharge-ready";
const AFFINITY_GUEST_NEW_BOX_BYPASS_TTL_MS = 5000;
const AFFINITY_GUEST_NEW_BOX_PREFERENCE_KEY =
  "affinity:guest-new-box-preference";
const AFFINITY_GUEST_ONE_TIME_PREFERENCE_KEY =
  "affinity:guest-one-time-preference";

let pendingLoginModalNewBoxAction = null;
let pendingGuestPurchaseModeBypassKey = "";
let pendingGuestPurchaseModeBypassMode = "";
let pendingGuestNewBoxBypassExpiresAt = 0;
let pendingGuestNewBoxBypassGlobalExpiresAt = 0;
let loginModalPreviousScrollY = 0;
let loginModalBodyScrollLocked = false;

function logAffinitySubscriptionDebug(message, payload = {}) {}

function getAffinityGuestNewBoxPreference() {
  const storageNames = ["sessionStorage", "localStorage"];

  for (let i = 0; i < storageNames.length; i += 1) {
    try {
      const value = String(
        window[storageNames[i]]?.getItem(
          AFFINITY_GUEST_NEW_BOX_PREFERENCE_KEY,
        ) || "",
      ).trim();

      if (value === "true") {
        return true;
      }
    } catch (_error) {}
  }

  return false;
}

function getAffinityGuestOneTimePreference() {
  const storageNames = ["sessionStorage", "localStorage"];

  for (let i = 0; i < storageNames.length; i += 1) {
    try {
      const value = String(
        window[storageNames[i]]?.getItem(
          AFFINITY_GUEST_ONE_TIME_PREFERENCE_KEY,
        ) || "",
      ).trim();

      if (value === "true") {
        return true;
      }
    } catch (_error) {}
  }

  return false;
}

function getAffinityGuestPurchasePreference() {
  if (getAffinityGuestNewBoxPreference()) {
    return "subscription";
  }

  if (getAffinityGuestOneTimePreference()) {
    return "one-time";
  }

  return "";
}

function setAffinityGuestNewBoxPreference() {
  const storageNames = ["sessionStorage", "localStorage"];

  storageNames.forEach((storageName) => {
    try {
      window[storageName]?.setItem(
        AFFINITY_GUEST_NEW_BOX_PREFERENCE_KEY,
        "true",
      );
      window[storageName]?.removeItem(AFFINITY_GUEST_ONE_TIME_PREFERENCE_KEY);
    } catch (_error) {}
  });
}

function setAffinityGuestOneTimePreference() {
  const storageNames = ["sessionStorage", "localStorage"];

  storageNames.forEach((storageName) => {
    try {
      window[storageName]?.setItem(
        AFFINITY_GUEST_ONE_TIME_PREFERENCE_KEY,
        "true",
      );
      window[storageName]?.removeItem(AFFINITY_GUEST_NEW_BOX_PREFERENCE_KEY);
    } catch (_error) {}
  });
}

function clearAffinityGuestPurchasePreferences() {
  const storageNames = ["sessionStorage", "localStorage"];

  storageNames.forEach((storageName) => {
    try {
      window[storageName]?.removeItem(AFFINITY_GUEST_NEW_BOX_PREFERENCE_KEY);
      window[storageName]?.removeItem(AFFINITY_GUEST_ONE_TIME_PREFERENCE_KEY);
    } catch (_error) {}
  });
}

function getAffinityRechargeConfig() {
  return window.AffinityRechargeConfig || {};
}

function hasAffinityLoginStateConfig() {
  const rechargeConfig = getAffinityRechargeConfig();
  return (
    Object.prototype.hasOwnProperty.call(rechargeConfig, "customerLoggedIn") ||
    Object.prototype.hasOwnProperty.call(rechargeConfig, "customerId")
  );
}

function getAffinityCustomerId() {
  const customerId = getAffinityRechargeConfig().customerId;
  return customerId ? String(customerId).trim() : "";
}

function isAffinityCustomerLoggedIn() {
  const rechargeConfig = getAffinityRechargeConfig();
  const isLoggedIn =
    rechargeConfig.customerLoggedIn === true ||
    Boolean(rechargeConfig.customerId);
  return isLoggedIn;
}

function getAffinityCustomerLoginHint() {
  try {
    const rawValue = window.sessionStorage?.getItem(
      AFFINITY_CUSTOMER_LOGIN_HINT_KEY,
    );
    if (!rawValue) return false;

    const timestamp = Number(rawValue);
    if (!Number.isFinite(timestamp)) {
      window.sessionStorage?.removeItem(AFFINITY_CUSTOMER_LOGIN_HINT_KEY);
      return false;
    }

    if (Date.now() - timestamp > AFFINITY_CUSTOMER_LOGIN_HINT_TTL_MS) {
      window.sessionStorage?.removeItem(AFFINITY_CUSTOMER_LOGIN_HINT_KEY);
      return false;
    }

    return true;
  } catch (_error) {
    return false;
  }
}

function setAffinityCustomerLoginHint() {
  try {
    window.sessionStorage?.setItem(
      AFFINITY_CUSTOMER_LOGIN_HINT_KEY,
      String(Date.now()),
    );
  } catch (_error) {}

  cachedAffinityCustomerLoginResolved = true;
  cachedAffinityCustomerLoggedIn = true;
}

function clearAffinityCustomerLoginHint() {
  try {
    window.sessionStorage?.removeItem(AFFINITY_CUSTOMER_LOGIN_HINT_KEY);
  } catch (_error) {}
}

function markAffinityCustomerLoginPending() {
  try {
    window.sessionStorage?.setItem(
      AFFINITY_CUSTOMER_LOGIN_PENDING_KEY,
      String(Date.now()),
    );
  } catch (_error) {}
}

function clearAffinityCustomerLoginPending() {
  try {
    window.sessionStorage?.removeItem(AFFINITY_CUSTOMER_LOGIN_PENDING_KEY);
  } catch (_error) {}
}

function markAffinityPortalRedirectAfterLoginPending() {
  try {
    window.sessionStorage?.setItem(
      AFFINITY_PORTAL_REDIRECT_AFTER_LOGIN_KEY,
      String(Date.now()),
    );
  } catch (_error) {}
}

function clearAffinityPortalRedirectAfterLoginPending() {
  try {
    window.sessionStorage?.removeItem(AFFINITY_PORTAL_REDIRECT_AFTER_LOGIN_KEY);
  } catch (_error) {}
}

function getAffinityPortalRedirectAfterLoginPending() {
  try {
    const rawValue = window.sessionStorage?.getItem(
      AFFINITY_PORTAL_REDIRECT_AFTER_LOGIN_KEY,
    );
    if (!rawValue) return false;

    const timestamp = Number(rawValue);
    if (!Number.isFinite(timestamp)) {
      clearAffinityPortalRedirectAfterLoginPending();
      return false;
    }

    if (Date.now() - timestamp > AFFINITY_CUSTOMER_LOGIN_HINT_TTL_MS) {
      clearAffinityPortalRedirectAfterLoginPending();
      return false;
    }

    return true;
  } catch (_error) {
    return false;
  }
}

function getAffinityCustomerLoginPending() {
  try {
    const rawValue = window.sessionStorage?.getItem(
      AFFINITY_CUSTOMER_LOGIN_PENDING_KEY,
    );
    if (!rawValue) return false;

    const timestamp = Number(rawValue);
    if (!Number.isFinite(timestamp)) {
      clearAffinityCustomerLoginPending();
      return false;
    }

    if (Date.now() - timestamp > AFFINITY_CUSTOMER_LOGIN_HINT_TTL_MS) {
      clearAffinityCustomerLoginPending();
      return false;
    }

    return true;
  } catch (_error) {
    return false;
  }
}

function markAffinitySubscriptionCheckoutPending() {
  const timestamp = String(Date.now());
  ["sessionStorage", "localStorage"].forEach((storageName) => {
    try {
      window[storageName]?.setItem(
        AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_KEY,
        timestamp,
      );
    } catch (_error) {}
  });
}

function clearAffinitySubscriptionCheckoutPending() {
  ["sessionStorage", "localStorage"].forEach((storageName) => {
    try {
      window[storageName]?.removeItem(
        AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_KEY,
      );
    } catch (_error) {}
  });
}

function hasAffinitySubscriptionCheckoutPending() {
  const storageNames = ["sessionStorage", "localStorage"];

  for (let i = 0; i < storageNames.length; i += 1) {
    try {
      const rawValue = window[storageNames[i]]?.getItem(
        AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_KEY,
      );
      if (!rawValue) continue;

      const timestamp = Number(rawValue);
      if (!Number.isFinite(timestamp)) {
        window[storageNames[i]]?.removeItem(
          AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_KEY,
        );
        continue;
      }

      if (
        Date.now() - timestamp >
        AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_TTL_MS
      ) {
        window[storageNames[i]]?.removeItem(
          AFFINITY_SUBSCRIPTION_CHECKOUT_PENDING_KEY,
        );
        continue;
      }

      return true;
    } catch (_error) {}
  }

  return false;
}

function currentUrlHasAffinityShopSignInParam() {
  try {
    const currentUrl = new URL(window.location.href);
    return (
      String(currentUrl.searchParams.get("shop_sign_in") || "").trim() ===
      "true"
    );
  } catch (_error) {
    return false;
  }
}

function hydrateAffinityCustomerLoginHintFromUrl() {
  if (isAffinityCustomerLoggedIn() || getAffinityCustomerLoginHint()) {
    clearAffinityCustomerLoginPending();
    return;
  }

  if (
    !currentUrlHasAffinityShopSignInParam() ||
    !getAffinityCustomerLoginPending()
  ) {
    return;
  }

  setAffinityCustomerLoginHint();
  clearAffinityCustomerLoginPending();
  logAffinitySubscriptionDebug(
    "Recovered logged-in customer state from login redirect.",
    {
      shopSignInParam: "true",
    },
  );
}

function getCachedAffinityCustomerLoggedInState() {
  if (isAffinityCustomerLoggedIn()) {
    return true;
  }

  if (
    getAffinityCustomerLoginHint() &&
    (currentUrlHasAffinityShopSignInParam() ||
      getAffinityCustomerLoginPending())
  ) {
    return true;
  }

  if (cachedAffinityCustomerLoginResolved) {
    return cachedAffinityCustomerLoggedIn;
  }

  return null;
}

function getAffinitySubscriptionPresenceStorageKey() {
  const customerId = getAffinityCustomerId();
  return customerId
    ? `affinity:recharge-subscription-presence:${customerId}`
    : "";
}

function getStoredAffinitySubscriptionPresence() {
  const storageKey = getAffinitySubscriptionPresenceStorageKey();
  if (!storageKey) return null;

  const storageNames = ["sessionStorage", "localStorage"];

  for (const storageName of storageNames) {
    try {
      const storage = window[storageName];
      const rawValue = storage?.getItem(storageKey);
      if (!rawValue) continue;

      const parsed = JSON.parse(rawValue);
      const timestamp = Number(parsed?.timestamp);
      const value = parsed?.value;

      if (
        !Number.isFinite(timestamp) ||
        typeof value !== "boolean" ||
        Date.now() - timestamp > AFFINITY_SUBSCRIPTION_PRESENCE_CACHE_TTL_MS
      ) {
        storage?.removeItem(storageKey);
        continue;
      }

      lastAffinitySubscriptionPresenceResolvedAt = timestamp;
      return value;
    } catch (_error) {}
  }

  return null;
}

function setStoredAffinitySubscriptionPresence(value) {
  const storageKey = getAffinitySubscriptionPresenceStorageKey();
  if (!storageKey || typeof value !== "boolean") return;
  const timestamp = Date.now();

  const payload = JSON.stringify({
    value,
    timestamp,
  });

  lastAffinitySubscriptionPresenceResolvedAt = timestamp;

  ["sessionStorage", "localStorage"].forEach((storageName) => {
    try {
      window[storageName]?.setItem(storageKey, payload);
    } catch (_error) {}
  });

  if (value === true) {
    clearAffinitySubscriptionCheckoutPending();
  }
}

function getKnownAffinitySubscriptionPresence() {
  if (typeof cachedHasRechargeSubscriptions === "boolean") {
    if (
      cachedHasRechargeSubscriptions === false &&
      hasAffinitySubscriptionCheckoutPending()
    ) {
      if (
        lastAffinitySubscriptionPresenceResolvedAt &&
        Date.now() - lastAffinitySubscriptionPresenceResolvedAt <
          AFFINITY_SUBSCRIPTION_PRESENCE_PENDING_RECHECK_MS
      ) {
        return false;
      }

      return null;
    }

    return cachedHasRechargeSubscriptions;
  }

  const storedPresence = getStoredAffinitySubscriptionPresence();
  if (typeof storedPresence === "boolean") {
    if (storedPresence === false && hasAffinitySubscriptionCheckoutPending()) {
      if (
        lastAffinitySubscriptionPresenceResolvedAt &&
        Date.now() - lastAffinitySubscriptionPresenceResolvedAt <
          AFFINITY_SUBSCRIPTION_PRESENCE_PENDING_RECHECK_MS
      ) {
        cachedHasRechargeSubscriptions = storedPresence;
        return cachedHasRechargeSubscriptions;
      }

      return null;
    }

    cachedHasRechargeSubscriptions = storedPresence;
    return cachedHasRechargeSubscriptions;
  }

  return null;
}

hydrateAffinityCustomerLoginHintFromUrl();

if (
  !isAffinityCustomerLoggedIn() &&
  !currentUrlHasAffinityShopSignInParam() &&
  !getAffinityCustomerLoginPending()
) {
  clearAffinityCustomerLoginHint();
}

async function resolveAffinityCustomerLoggedIn() {
  const cachedState = getCachedAffinityCustomerLoggedInState();

  if (typeof cachedState === "boolean") {
    return cachedState;
  }

  const session = await getAffinityRechargeSession();
  return Boolean(session?.customerId);
}

function getAffinityPortalUrl() {
  return (
    String(getAffinityRechargeConfig().customerPortalUrl || "").trim() ||
    AFFINITY_RECHARGE_PORTAL_URL_FALLBACK
  );
}

function getAffinityLoginUrl() {
  const configuredLoginUrl = String(
    getAffinityRechargeConfig().accountLoginUrl || "",
  ).trim();

  if (configuredLoginUrl) {
    return new URL(configuredLoginUrl, window.location.origin);
  }

  return new URL("/account/login", window.location.origin);
}

function getAddToCartButtonTextElement(button) {
  return (
    button?.querySelector(".add-to-cart-text__content > span > span") ||
    button?.querySelector(".add-to-cart-text__content")
  );
}

function setAddToCartButtonText(button, nextLabel) {
  const textElement = getAddToCartButtonTextElement(button);
  if (!(textElement instanceof HTMLElement)) return;
  textElement.textContent = String(nextLabel || "").trim();
}

function getAffinityAddToBoxLabel(source) {
  const productForm =
    source instanceof HTMLElement && source.matches("product-form-component")
      ? source
      : source instanceof HTMLElement
        ? source.closest("product-form-component")
        : null;

  const customLabel = String(
    productForm?.dataset.affinityAddToBoxLabel || "",
  ).trim();

  return customLabel || AFFINITY_ADD_TO_BOX_LABEL;
}

function isAddToCartButtonShowingAddToBox(button) {
  const label =
    getAddToCartButtonTextElement(button)?.textContent?.trim() || "";
  return label === getAffinityAddToBoxLabel(button);
}

function buildAffinityLoginRedirectUrl() {
  const loginUrl = getAffinityLoginUrl();
  loginUrl.searchParams.set("return_url", window.location.href);
  return loginUrl.toString();
}

function redirectToAffinityLogin({ redirectToPortalAfterLogin = false } = {}) {
  if (redirectToPortalAfterLogin) {
    markAffinityPortalRedirectAfterLoginPending();
  } else {
    clearAffinityPortalRedirectAfterLoginPending();
  }

  markAffinityCustomerLoginPending();
  window.location.href = buildAffinityLoginRedirectUrl();
}

function getLoginModal() {
  const modal = document.getElementById("login-modal");
  return modal instanceof HTMLDialogElement ? modal : null;
}

function syncLoginModalScrollLockState() {
  const hasOpenScrollLockDialog = Boolean(
    document.querySelector("dialog[open][scroll-lock]"),
  );

  if (hasOpenScrollLockDialog) {
    document.documentElement.setAttribute("scroll-lock", "");
    return;
  }

  document.documentElement.removeAttribute("scroll-lock");
}

function lockLoginModalBodyScroll() {
  if (loginModalBodyScrollLocked) return;

  loginModalPreviousScrollY = window.scrollY;
  document.body.style.width = "100%";
  document.body.style.position = "fixed";
  document.body.style.top = `-${loginModalPreviousScrollY}px`;
  loginModalBodyScrollLocked = true;
  syncLoginModalScrollLockState();
}

function unlockLoginModalBodyScroll() {
  if (!loginModalBodyScrollLocked) {
    syncLoginModalScrollLockState();
    return;
  }

  document.body.style.width = "";
  document.body.style.position = "";
  document.body.style.top = "";
  window.scrollTo({ top: loginModalPreviousScrollY, behavior: "instant" });
  loginModalBodyScrollLocked = false;
  syncLoginModalScrollLockState();
}

function closeLoginModal() {
  const modal = getLoginModal();
  if (!modal?.open) return;
  modal.close();
  unlockLoginModalBodyScroll();
  pendingLoginModalNewBoxAction = null;
}

function syncLoginModalTargets(modal) {
  const existingAccountButton = modal.querySelector(
    "[data-login-modal-existing-account]",
  );

  if (existingAccountButton instanceof HTMLButtonElement) {
    existingAccountButton.dataset.loginTarget = buildAffinityLoginRedirectUrl();
  }
}

function bindLoginModal(modal) {
  if (modal.dataset.affinityBound === "true") return;

  const closeButton = modal.querySelector("[data-login-modal-close]");

  if (closeButton instanceof HTMLButtonElement) {
    closeButton.addEventListener("click", () => {
      closeLoginModal();
    });
  }

  modal.addEventListener("click", (event) => {
    if (event.target === modal) {
      closeLoginModal();
    }
  });

  modal.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeLoginModal();
  });

  const existingAccountButton = modal.querySelector(
    "[data-login-modal-existing-account]",
  );
  if (existingAccountButton instanceof HTMLButtonElement) {
    existingAccountButton.addEventListener("click", () => {
      clearAffinityGuestPurchasePreferences();
      closeLoginModal();
      redirectToAffinityLogin({ redirectToPortalAfterLogin: true });
    });
  }

  const newBoxButton = modal.querySelector("[data-login-modal-new-box]");
  if (newBoxButton instanceof HTMLButtonElement) {
    newBoxButton.addEventListener("click", () => {
      const pendingAction = pendingLoginModalNewBoxAction;
      setAffinityGuestNewBoxPreference();
      closeLoginModal();

      if (typeof pendingAction === "function") {
        pendingAction();
      }
    });
  }

  modal.dataset.affinityBound = "true";
}

function openLoginModal(options = {}) {
  const modal = getLoginModal();
  const { onContinueAsNewBox = null } = options;

  if (!modal || typeof modal.showModal !== "function") {
    redirectToAffinityLogin({ redirectToPortalAfterLogin: true });
    return false;
  }

  pendingLoginModalNewBoxAction =
    typeof onContinueAsNewBox === "function" ? onContinueAsNewBox : null;
  bindLoginModal(modal);
  syncLoginModalTargets(modal);

  if (!modal.open) {
    modal.showModal();
    lockLoginModalBodyScroll();
  }

  return true;
}

function getGuestNewBoxBypassKey(productForm) {
  if (!(productForm instanceof HTMLElement)) return "";

  return (
    String(productForm.id || "").trim() ||
    String(productForm.dataset.productId || "").trim() ||
    String(productForm.dataset.productHandle || "").trim()
  );
}

function armGuestPurchaseModeBypass(productForm, mode = "subscription") {
  pendingGuestPurchaseModeBypassKey = getGuestNewBoxBypassKey(productForm);
  pendingGuestPurchaseModeBypassMode =
    String(mode || "").trim() || "subscription";
  pendingGuestNewBoxBypassExpiresAt =
    Date.now() + AFFINITY_GUEST_NEW_BOX_BYPASS_TTL_MS;
  pendingGuestNewBoxBypassGlobalExpiresAt =
    Date.now() + AFFINITY_GUEST_NEW_BOX_BYPASS_TTL_MS;
}

function consumeGuestPurchaseModeBypass(productForm) {
  if (
    Number.isFinite(pendingGuestNewBoxBypassGlobalExpiresAt) &&
    Date.now() <= pendingGuestNewBoxBypassGlobalExpiresAt
  ) {
    const mode = pendingGuestPurchaseModeBypassMode || "subscription";
    pendingGuestPurchaseModeBypassKey = "";
    pendingGuestPurchaseModeBypassMode = "";
    pendingGuestNewBoxBypassExpiresAt = 0;
    pendingGuestNewBoxBypassGlobalExpiresAt = 0;
    return mode;
  }

  if (
    !pendingGuestPurchaseModeBypassKey ||
    !Number.isFinite(pendingGuestNewBoxBypassExpiresAt)
  ) {
    return "";
  }

  if (Date.now() > pendingGuestNewBoxBypassExpiresAt) {
    pendingGuestPurchaseModeBypassKey = "";
    pendingGuestPurchaseModeBypassMode = "";
    pendingGuestNewBoxBypassExpiresAt = 0;
    pendingGuestNewBoxBypassGlobalExpiresAt = 0;
    return "";
  }

  const matches =
    pendingGuestPurchaseModeBypassKey === getGuestNewBoxBypassKey(productForm);

  if (matches) {
    const mode = pendingGuestPurchaseModeBypassMode || "subscription";
    pendingGuestPurchaseModeBypassKey = "";
    pendingGuestPurchaseModeBypassMode = "";
    pendingGuestNewBoxBypassExpiresAt = 0;
    pendingGuestNewBoxBypassGlobalExpiresAt = 0;
    return mode;
  }

  return "";
}

function getDefaultSubscriptionSellingPlanValue(select) {
  if (!(select instanceof HTMLSelectElement)) return "";

  const subscriptionOption = Array.from(select.options).find(
    (option) =>
      option instanceof HTMLOptionElement && String(option.value || "").trim(),
  );

  return subscriptionOption instanceof HTMLOptionElement
    ? String(subscriptionOption.value || "").trim()
    : "";
}

function isPdpSubscriptionWidgetEnabled(productForm) {
  return (
    productForm instanceof HTMLElement &&
    productForm.dataset.pdpSubscriptionWidgetEnabled === "true"
  );
}

function getPdpSubscriptionWidget(productForm) {
  if (!(productForm instanceof HTMLElement)) return null;

  const widget = productForm.querySelector("[data-pdp-subscription-widget]");
  return widget instanceof HTMLElement ? widget : null;
}

function getPdpManualPurchaseModeOverride(productForm) {
  if (!(productForm instanceof HTMLElement)) return "";

  const mode = String(productForm.dataset.pdpPurchaseModeOverride || "").trim();
  return mode === "subscription" || mode === "one-time" ? mode : "";
}

function setPdpManualPurchaseModeOverride(productForm, mode = "") {
  if (!(productForm instanceof HTMLElement)) return;

  const normalizedMode = String(mode || "").trim();

  if (normalizedMode === "subscription" || normalizedMode === "one-time") {
    productForm.dataset.pdpPurchaseModeOverride = normalizedMode;
    return;
  }

  delete productForm.dataset.pdpPurchaseModeOverride;
}

function getVoucherSet(config) {
  const setValue = config?.set;

  if (Array.isArray(setValue)) {
    return setValue[0] && typeof setValue[0] === "object" ? setValue[0] : {};
  }

  return setValue && typeof setValue === "object" ? setValue : {};
}

function normalizeVoucherDiscountDisplay(value, fallback = "10%") {
  const rawValue = String(value || "").trim();
  if (!rawValue) return fallback;

  const numericValue = Number(rawValue.replace(/[^\d.]/g, ""));

  if (Number.isFinite(numericValue) && numericValue > 0) {
    return `${numericValue}%`;
  }

  return fallback;
}

function getVoucherDiscountPercent(value, fallback = 10) {
  const normalizedDisplay = normalizeVoucherDiscountDisplay(
    value,
    `${fallback}%`,
  );
  const parsedValue = Number(normalizedDisplay.replace(/[^\d.]/g, ""));

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return fallback;
  }

  return Math.min(parsedValue, 100);
}

function getResolvedVoucherConfigForPdp() {
  const activeContext = normalizeVoucherContext(
    window.Theme?.shopifyStorefront?.activeVoucherContext,
  );

  if (activeContext?.config && typeof activeContext.config === "object") {
    return activeContext.config;
  }

  const requestedHandle =
    activeContext?.handle ||
    getVoucherHandleFromURL() ||
    cachedVoucherHandle ||
    DEFAULT_VOUCHER_HANDLE;

  return (
    buildVoucherContext(requestedHandle)?.config ||
    buildVoucherContext(DEFAULT_VOUCHER_HANDLE)?.config ||
    null
  );
}

function getPdpVoucherDiscountDisplay(productForm) {
  const widget = getPdpSubscriptionWidget(productForm);
  const fallbackDisplay = String(
    widget?.dataset.defaultDiscountDisplay || "10%",
  ).trim();
  const voucherConfig = getResolvedVoucherConfigForPdp();
  const voucherSet = getVoucherSet(voucherConfig);

  return normalizeVoucherDiscountDisplay(
    voucherSet.discount_value,
    fallbackDisplay,
  );
}

function formatPdpMoney(cents, currencyCode = "USD") {
  const normalizedCents = Number(cents || 0);
  const normalizedCurrencyCode = String(currencyCode || "USD").trim() || "USD";

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: normalizedCurrencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(normalizedCents / 100);
}

function getPdpEffectivePurchaseMode(productForm) {
  const manualOverride = getPdpManualPurchaseModeOverride(productForm);
  if (manualOverride) {
    return manualOverride;
  }

  const guestPreference = getAffinityGuestPurchasePreference();
  if (guestPreference === "one-time") {
    return "one-time";
  }

  const sellingPlanSelect =
    productForm instanceof HTMLElement
      ? productForm.querySelector("[data-product-selling-plan]")
      : null;
  const selectedSellingPlanId = normalizeSellingPlanId(
    sellingPlanSelect instanceof HTMLSelectElement
      ? sellingPlanSelect.value
      : "",
  );

  return selectedSellingPlanId ? "subscription" : "one-time";
}

function syncPdpSubscriptionWidget(productForm, { variant = null } = {}) {
  if (!isPdpSubscriptionWidgetEnabled(productForm)) return;

  const widget = getPdpSubscriptionWidget(productForm);
  if (!(widget instanceof HTMLElement)) return;

  const subscriptionCard = widget.querySelector(
    '[data-pdp-option-card="subscription"]',
  );
  const oneTimeCard = widget.querySelector('[data-pdp-option-card="one-time"]');
  const subscriptionDot = widget.querySelector(
    '[data-pdp-option-dot="subscription"]',
  );
  const oneTimeDot = widget.querySelector('[data-pdp-option-dot="one-time"]');
  const subscriptionIndicator = widget.querySelector(
    '[data-pdp-option-indicator="subscription"]',
  );
  const oneTimeIndicator = widget.querySelector(
    '[data-pdp-option-indicator="one-time"]',
  );
  const banner = widget.querySelector("[data-pdp-subscription-banner]");
  const benefitDiscount = widget.querySelector(
    "[data-pdp-subscription-benefit-discount]",
  );
  const subscriptionOriginalPrice = widget.querySelector(
    "[data-pdp-subscription-original-price]",
  );
  const subscriptionDiscountedPrice = widget.querySelector(
    "[data-pdp-subscription-discounted-price]",
  );
  const oneTimePrice = widget.querySelector("[data-pdp-one-time-price]");

  if (variant?.price != null) {
    widget.dataset.basePriceCents = String(variant.price);
  }

  const basePriceCents = Number(widget.dataset.basePriceCents || 0);
  const currencyCode = String(widget.dataset.currencyCode || "USD").trim();
  const discountDisplay = getPdpVoucherDiscountDisplay(productForm);
  const discountPercent = getVoucherDiscountPercent(discountDisplay, 10);
  const discountedPriceCents = Math.round(
    basePriceCents * ((100 - discountPercent) / 100),
  );
  const activeMode = getPdpEffectivePurchaseMode(productForm);

  if (banner instanceof HTMLElement) {
    banner.textContent = `BEST VALUE • SAVE ${discountDisplay}`;
  }

  if (benefitDiscount instanceof HTMLElement) {
    // benefitDiscount.textContent = `${discountDisplay} Off`;
    const template = window.themeSettings.subscriptionBenefits;
    if (template) {
      const lines = template.split('\n');
      const firstLineTemplate = lines[0].trim();
      benefitDiscount.textContent = firstLineTemplate.replace('{discount}%', discountDisplay);
    }
  }

  if (subscriptionOriginalPrice instanceof HTMLElement) {
    subscriptionOriginalPrice.textContent = formatPdpMoney(
      basePriceCents,
      currencyCode,
    );
  }

  if (subscriptionDiscountedPrice instanceof HTMLElement) {
    subscriptionDiscountedPrice.textContent = formatPdpMoney(
      discountedPriceCents,
      currencyCode,
    );
  }

  if (oneTimePrice instanceof HTMLElement) {
    oneTimePrice.textContent = formatPdpMoney(basePriceCents, currencyCode);
  }

  const toggleCardState = (card, indicator, dot, isActive) => {
    if (!(card instanceof HTMLElement)) return;

    card.dataset.active = isActive ? "true" : "false";
    card.setAttribute("aria-pressed", isActive ? "true" : "false");
    card.classList.toggle("border-pepper-600", isActive);
    card.classList.toggle("ring-1", isActive);
    card.classList.toggle("ring-pepper-600", isActive);
    card.classList.toggle("border-coal-700", !isActive);

    if (indicator instanceof HTMLElement) {
      indicator.classList.toggle("border-pepper-600", isActive);
      indicator.classList.toggle("border-coal-700", !isActive);
    }

    if (dot instanceof HTMLElement) {
      dot.classList.toggle("opacity-100", isActive);
      dot.classList.toggle("opacity-0", !isActive);
    }
  };

  toggleCardState(
    subscriptionCard,
    subscriptionIndicator,
    subscriptionDot,
    activeMode === "subscription",
  );
  toggleCardState(
    oneTimeCard,
    oneTimeIndicator,
    oneTimeDot,
    activeMode === "one-time",
  );
}

function setPdpPurchaseMode(
  productForm,
  mode,
  { dispatchChange = true, persistOverride = true } = {},
) {
  if (!isPdpSubscriptionWidgetEnabled(productForm)) return;

  const nextMode = mode === "one-time" ? "one-time" : "subscription";

  if (persistOverride) {
    setPdpManualPurchaseModeOverride(productForm, nextMode);
  }

  const sellingPlanSelect =
    productForm instanceof HTMLElement
      ? productForm.querySelector("[data-product-selling-plan]")
      : null;

  if (sellingPlanSelect instanceof HTMLSelectElement) {
    if (nextMode === "one-time") {
      sellingPlanSelect.value = "";
    } else if (!sellingPlanSelect.value) {
      sellingPlanSelect.value =
        getDefaultSubscriptionSellingPlanValue(sellingPlanSelect);
    }

    if (dispatchChange) {
      sellingPlanSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }
  } else {
    syncPdpSubscriptionWidget(productForm);
  }
}

function submitGuestPurchaseChoice(
  productForm,
  submitter,
  mode = "subscription",
) {
  if (!(productForm instanceof HTMLElement)) return;

  const form = productForm.querySelector("form");
  if (!(form instanceof HTMLFormElement)) return;

  const sellingPlanSelect = productForm.querySelector(
    "[data-product-selling-plan]",
  );
  const previousSellingPlanValue =
    sellingPlanSelect instanceof HTMLSelectElement
      ? String(sellingPlanSelect.value || "").trim()
      : "";

  if (isPdpSubscriptionWidgetEnabled(productForm)) {
    setPdpManualPurchaseModeOverride(productForm, mode);
  }

  if (sellingPlanSelect instanceof HTMLSelectElement) {
    if (mode === "one-time") {
      sellingPlanSelect.value = "";
    } else if (!sellingPlanSelect.value) {
      sellingPlanSelect.value =
        previousSellingPlanValue ||
        getDefaultSubscriptionSellingPlanValue(sellingPlanSelect);
    }

    sellingPlanSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  armGuestPurchaseModeBypass(productForm, mode);

  if (typeof form.requestSubmit === "function") {
    form.requestSubmit(
      submitter instanceof HTMLButtonElement ? submitter : undefined,
    );
  } else {
    form.submit();
  }

  window.setTimeout(() => {
    if (!(sellingPlanSelect instanceof HTMLSelectElement)) return;

    if (mode === "one-time") {
      sellingPlanSelect.value =
        previousSellingPlanValue ||
        getDefaultSubscriptionSellingPlanValue(sellingPlanSelect);
      sellingPlanSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }, 0);
}

function buildAffinityPortalUrl(options = {}) {
  const { autoOpenBundleEditor = false, openSubscriptions = false } = options;
  const currentUrl = new URL(window.location.href);
  const portalUrl = new URL(getAffinityPortalUrl());
  const previewParamKeys = ["preview_theme_id", "_fd", "pb"];

  previewParamKeys.forEach((key) => {
    const value = String(currentUrl.searchParams.get(key) || "").trim();
    if (!value) return;
    portalUrl.searchParams.set(key, value);
  });

  if (openSubscriptions) {
    portalUrl.pathname = portalUrl.pathname.replace(
      /\/overview\/?$/,
      "/subscriptions",
    );
  }

  if (autoOpenBundleEditor) {
    portalUrl.searchParams.set(AFFINITY_PORTAL_AUTO_OPEN_BUNDLE_PARAM, "1");
  }

  return portalUrl.toString();
}

function persistAffinityPortalSubscriptionsIntent() {
  const storageNames = ["sessionStorage", "localStorage"];

  storageNames.forEach((storageName) => {
    try {
      const storage = window[storageName];
      if (!storage) return;
      storage.setItem(AFFINITY_PORTAL_OPEN_SUBSCRIPTIONS_STORAGE_KEY, "1");
    } catch (_error) {}
  });
}

function normalizeAffinitySubscriptionStatus(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function isAffinityActivePortalSubscription(subscription) {
  return (
    !subscription?.cancelled_at &&
    normalizeAffinitySubscriptionStatus(subscription?.status) === "active"
  );
}

async function resolveAffinityPortalDestination() {
  const session = await getAffinityRechargeSession();

  if (!session) {
    return {
      hasSubscriptions: false,
      shouldOpenSubscriptions: false,
    };
  }

  const response = await window.recharge.subscription.listSubscriptions(
    session,
    {
      limit: 50,
      sort_by: "id-desc",
    },
  );
  const subscriptions = Array.isArray(response?.subscriptions)
    ? response.subscriptions
    : [];
  const hasSubscriptions = subscriptions.length > 0;
  const hasActiveSubscriptions = subscriptions.some(
    isAffinityActivePortalSubscription,
  );

  return {
    hasSubscriptions,
    shouldOpenSubscriptions: hasSubscriptions && !hasActiveSubscriptions,
  };
}

async function maybeRedirectToAffinityPortalAfterLogin() {
  if (!getAffinityPortalRedirectAfterLoginPending()) {
    return false;
  }

  const isLoggedIn = await resolveAffinityCustomerLoggedIn();
  if (!isLoggedIn) {
    return false;
  }

  const portalDestination = await resolveAffinityPortalDestination();
  clearAffinityPortalRedirectAfterLoginPending();

  if (!portalDestination.hasSubscriptions) {
    return false;
  }

  if (portalDestination.shouldOpenSubscriptions) {
    persistAffinityPortalSubscriptionsIntent();
    window.location.href = buildAffinityPortalUrl({
      openSubscriptions: true,
    });
    return true;
  }

  persistAffinityPortalBundleEditorIntent();
  window.location.href = buildAffinityPortalUrl({
    autoOpenBundleEditor: true,
  });
  return true;
}

function persistAffinityPortalBundleEditorIntent() {
  const storageNames = ["sessionStorage", "localStorage"];

  storageNames.forEach((storageName) => {
    try {
      const storage = window[storageName];
      if (!storage) return;
      storage.setItem(AFFINITY_PORTAL_AUTO_OPEN_BUNDLE_STORAGE_KEY, "1");
    } catch (_error) {}
  });
}

async function maybeRedirectToAffinityPortalFromCta() {
  const isLoggedIn = await resolveAffinityCustomerLoggedIn();
  if (!isLoggedIn) {
    return false;
  }

  const portalDestination = await resolveAffinityPortalDestination();
  if (!portalDestination.hasSubscriptions) {
    return false;
  }

  if (portalDestination.shouldOpenSubscriptions) {
    persistAffinityPortalSubscriptionsIntent();
    window.location.href = buildAffinityPortalUrl({
      openSubscriptions: true,
    });
    return true;
  }

  persistAffinityPortalBundleEditorIntent();
  window.location.href = buildAffinityPortalUrl({
    autoOpenBundleEditor: true,
  });
  return true;
}

window.GoodChopAffinity = {
  ...(window.GoodChopAffinity || {}),
  maybeRedirectToAffinityPortalFromCta,
};

void maybeRedirectToAffinityPortalAfterLogin();

async function waitForAffinityRechargeClient(
  timeoutMs = AFFINITY_RECHARGE_READY_TIMEOUT,
) {
  const isReady = () =>
    Boolean(
      window.recharge?.auth?.loginShopifyAppProxy &&
      window.recharge?.subscription?.listSubscriptions,
    );

  if (isReady()) {
    return true;
  }

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) =>
      window.setTimeout(resolve, AFFINITY_RECHARGE_READY_INTERVAL),
    );

    if (isReady()) {
      return true;
    }
  }

  return false;
}

async function waitForAffinityLoginStateConfig(timeoutMs = 1500) {
  if (hasAffinityLoginStateConfig()) {
    return true;
  }

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));

    if (hasAffinityLoginStateConfig()) {
      return true;
    }
  }

  return hasAffinityLoginStateConfig();
}

async function getAffinityRechargeSession() {
  if (affinityRechargeSession) {
    cachedAffinityCustomerLoginResolved = true;
    cachedAffinityCustomerLoggedIn = true;
    logAffinitySubscriptionDebug("Using cached Recharge session.", {
      hasCustomerId: Boolean(affinityRechargeSession.customerId),
      customerId: affinityRechargeSession.customerId || null,
    });
    return affinityRechargeSession;
  }

  if (affinityRechargeSessionRequest) {
    return affinityRechargeSessionRequest;
  }

  const hasRechargeClient = await waitForAffinityRechargeClient();

  if (!hasRechargeClient) {
    cachedAffinityCustomerLoginResolved = true;
    cachedAffinityCustomerLoggedIn = false;
    logAffinitySubscriptionDebug(
      "Recharge client unavailable while resolving session.",
    );
    return null;
  }

  affinityRechargeSessionRequest = Promise.resolve()
    .then(async () => {
      try {
        const session = await window.recharge.auth.loginShopifyAppProxy();
        affinityRechargeSession =
          session && session.customerId ? session : null;
        cachedAffinityCustomerLoginResolved = true;
        cachedAffinityCustomerLoggedIn = Boolean(affinityRechargeSession);
        logAffinitySubscriptionDebug("Resolved Recharge session.", {
          hasSession: Boolean(session),
          hasCustomerId: Boolean(session?.customerId),
          customerId: session?.customerId || null,
        });
        return affinityRechargeSession;
      } catch (error) {
        affinityRechargeSession = null;
        cachedAffinityCustomerLoginResolved = true;
        cachedAffinityCustomerLoggedIn = false;
        logAffinitySubscriptionDebug("Failed to resolve Recharge session.", {
          error: error instanceof Error ? error.message : String(error),
        });
        return null;
      }
    })
    .finally(() => {
      affinityRechargeSessionRequest = null;
    });

  return affinityRechargeSessionRequest;
}

async function customerHasRechargeSubscriptions() {
  const knownPresence = getKnownAffinitySubscriptionPresence();
  if (typeof knownPresence === "boolean") {
    logAffinitySubscriptionDebug("Using cached subscription presence.", {
      hasRechargeSubscriptions: knownPresence,
    });
    return knownPresence;
  }

  if (rechargeSubscriptionPresenceRequest) {
    return rechargeSubscriptionPresenceRequest;
  }

  const session = await getAffinityRechargeSession();

  if (!session) {
    logAffinitySubscriptionDebug(
      "No Recharge session found while checking subscriptions.",
    );
    return false;
  }

  rechargeSubscriptionPresenceRequest = Promise.resolve()
    .then(async () => {
      const response = await window.recharge.subscription.listSubscriptions(
        session,
        {
          limit: 1,
          sort_by: "id-desc",
        },
      );
      const subscriptions = Array.isArray(response?.subscriptions)
        ? response.subscriptions
        : [];

      cachedHasRechargeSubscriptions = subscriptions.length > 0;
      setStoredAffinitySubscriptionPresence(cachedHasRechargeSubscriptions);
      logAffinitySubscriptionDebug("Resolved Recharge subscriptions.", {
        hasRechargeSubscriptions: cachedHasRechargeSubscriptions,
        subscriptionCount: subscriptions.length,
      });
      return cachedHasRechargeSubscriptions;
    })
    .catch((error) => {
      console.error(
        "[Affinity Recharge] Failed to resolve subscription status from PLP.",
        error,
      );
      logAffinitySubscriptionDebug(
        "Failed to resolve Recharge subscriptions.",
        {
          error: error instanceof Error ? error.message : String(error),
        },
      );
      cachedHasRechargeSubscriptions = null;
      return false;
    })
    .finally(() => {
      rechargeSubscriptionPresenceRequest = null;
    });

  return rechargeSubscriptionPresenceRequest;
}

async function bootstrapAffinitySubscriptionPresence() {
  const knownPresence = getKnownAffinitySubscriptionPresence();
  if (typeof knownPresence === "boolean") {
    return knownPresence;
  }

  if (affinitySubscriptionBootstrapRequest) {
    return affinitySubscriptionBootstrapRequest;
  }

  affinitySubscriptionBootstrapRequest = Promise.resolve()
    .then(async () => {
      const hasLoginConfig = await waitForAffinityLoginStateConfig();

      if (
        !hasLoginConfig ||
        getCachedAffinityCustomerLoggedInState() !== true
      ) {
        return false;
      }

      await getAffinityRechargeSession();
      return customerHasRechargeSubscriptions();
    })
    .finally(() => {
      affinitySubscriptionBootstrapRequest = null;
    });

  return affinitySubscriptionBootstrapRequest;
}

void bootstrapAffinitySubscriptionPresence();
window.addEventListener(AFFINITY_RECHARGE_READY_EVENT, () => {
  void bootstrapAffinitySubscriptionPresence();
});

/**
 * Parses a collection id array stored in a dataset attribute.
 * @param {string | undefined} value
 * @returns {string[]}
 */
function parseCollectionIds(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return [String(value)];
  }

  if (typeof value !== "string" || !value.trim()) return [];

  try {
    return parseCollectionIds(JSON.parse(value));
  } catch (_error) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
}

let cachedVoucherHandle = "";
let cachedVoucherTag = "";
let cachedVoucherName = "";
let voucherTagRequest = null;
const VOUCHER_HANDLE_STORAGE_KEY = "gc_voucher_handle";
const VOUCHER_TAG_STORAGE_KEY = "gc_voucher_tag";
const VOUCHER_NAME_STORAGE_KEY = "gc_voucher_name";
const VOUCHER_CONTEXT_COOKIE_KEY = "gc_voucher_context";
const VOUCHER_CONTEXT_READY_EVENT = "gc:voucher-context-ready";
const VOUCHER_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
const DEFAULT_VOUCHER_HANDLE = "default";

function logVoucherDebug(message, payload = {}) {
  void message;
  void payload;
}

function getVoucherMetaobjectEntry(handle) {
  const entries = Theme?.shopifyStorefront?.voucherMetaobjectEntries || {};

  if (!handle || !entries || typeof entries !== "object") return null;

  return entries[handle] && typeof entries[handle] === "object"
    ? entries[handle]
    : null;
}

function parseVoucherConfigValue(rawConfig) {
  const normalizedConfig = String(rawConfig || "").trim();
  if (!normalizedConfig) return null;

  try {
    return JSON.parse(normalizedConfig);
  } catch (_error) {
    // Shopify Liquid can serialize JSON metaobject fields using Ruby hash rockets.
  }

  try {
    return JSON.parse(
      normalizedConfig.replace(/=>/g, ":").replace(/\bnil\b/g, "null"),
    );
  } catch (_error) {
    return null;
  }
}

function getVoucherTagFromThemeConfig(handle) {
  const entry = getVoucherMetaobjectEntry(handle);
  if (!entry) return "";

  const directTag = String(entry.flTag || "").trim();
  if (directTag) {
    logVoucherDebug("Resolved fl_tag from Theme voucher map.", {
      handle,
      flTag: directTag,
      source: "direct-field",
      entry,
    });
    return directTag;
  }

  const rawConfig = String(entry.config || "").trim();
  if (!rawConfig) {
    logVoucherDebug("Voucher entry found, but no config was available.", {
      handle,
      entry,
    });
    return "";
  }

  try {
    const parsedConfig = parseVoucherConfigValue(rawConfig);
    if (!parsedConfig) {
      logVoucherDebug("Voucher config could not be parsed.", {
        handle,
        entry,
      });
      return "";
    }

    const configTag = String(parsedConfig?.fl_tag || "").trim();

    if (configTag) {
      logVoucherDebug("Resolved fl_tag from voucher config JSON.", {
        handle,
        flTag: configTag,
        source: "config-json",
        entry,
      });
    } else {
      logVoucherDebug("Voucher config JSON did not contain fl_tag.", {
        handle,
        parsedConfig,
        entry,
      });
    }

    return configTag;
  } catch (_error) {
    return "";
  }
}

function getDefaultVoucherTag() {
  const defaultTag = getVoucherTagFromThemeConfig(DEFAULT_VOUCHER_HANDLE);

  if (defaultTag) {
    logVoucherDebug("Resolved default fl_tag from default voucher entry.", {
      handle: DEFAULT_VOUCHER_HANDLE,
      flTag: defaultTag,
    });
  } else {
    logVoucherDebug("Default voucher entry was missing fl_tag.", {
      handle: DEFAULT_VOUCHER_HANDLE,
    });
  }

  return defaultTag;
}

function getVoucherNameFromThemeConfig(handle) {
  const entry = getVoucherMetaobjectEntry(handle);
  return String(entry?.voucherName || "").trim();
}

function getVoucherConfigFromThemeConfig(handle) {
  const entry = getVoucherMetaobjectEntry(handle);
  const parsedConfig = parseVoucherConfigValue(entry?.config || "");
  return parsedConfig && typeof parsedConfig === "object" ? parsedConfig : null;
}

function buildVoucherContext(handle) {
  const normalizedHandle = String(handle || "")
    .trim()
    .toLowerCase();
  if (!normalizedHandle) return null;

  const tag = getVoucherTagFromThemeConfig(normalizedHandle);
  if (!tag) return null;

  return {
    handle: normalizedHandle,
    tag,
    voucherName: getVoucherNameFromThemeConfig(normalizedHandle),
    config: getVoucherConfigFromThemeConfig(normalizedHandle),
  };
}

function normalizeVoucherContext(rawContext) {
  if (!rawContext || typeof rawContext !== "object") return null;

  const handle = String(rawContext.handle || "")
    .trim()
    .toLowerCase();
  const tag = String(rawContext.tag || rawContext.flTag || "").trim();
  const voucherName = String(rawContext.voucherName || "").trim();
  const config =
    rawContext.config && typeof rawContext.config === "object"
      ? rawContext.config
      : null;

  if (!handle || !tag) return null;

  return {
    handle,
    tag,
    voucherName,
    config,
  };
}

function readSessionStorageValue(key) {
  try {
    return String(window.sessionStorage.getItem(key) || "").trim();
  } catch (_error) {
    return "";
  }
}

function writeSessionStorageValue(key, value) {
  try {
    if (value) {
      window.sessionStorage.setItem(key, value);
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch (_error) {
    // Ignore storage failures.
  }
}

function writeVoucherContextToSession(context) {
  const normalizedContext = normalizeVoucherContext(context);

  if (
    !normalizedContext ||
    normalizedContext.handle === DEFAULT_VOUCHER_HANDLE
  ) {
    writeSessionStorageValue(VOUCHER_HANDLE_STORAGE_KEY, "");
    writeSessionStorageValue(VOUCHER_TAG_STORAGE_KEY, "");
    writeSessionStorageValue(VOUCHER_NAME_STORAGE_KEY, "");
    return;
  }

  writeSessionStorageValue(
    VOUCHER_HANDLE_STORAGE_KEY,
    normalizedContext.handle,
  );
  writeSessionStorageValue(VOUCHER_TAG_STORAGE_KEY, normalizedContext.tag);
  writeSessionStorageValue(
    VOUCHER_NAME_STORAGE_KEY,
    normalizedContext.voucherName,
  );
}

function readVoucherContextFromCookie() {
  try {
    const cookies = String(document.cookie || "").split("; ");
    const match = cookies.find((cookie) =>
      cookie.startsWith(`${VOUCHER_CONTEXT_COOKIE_KEY}=`),
    );

    if (!match) return null;

    const encodedValue = match.slice(VOUCHER_CONTEXT_COOKIE_KEY.length + 1);
    return normalizeVoucherContext(
      JSON.parse(decodeURIComponent(encodedValue)),
    );
  } catch (_error) {
    return null;
  }
}

function writeVoucherContextToCookie(context) {
  const normalizedContext = normalizeVoucherContext(context);

  if (
    !normalizedContext ||
    normalizedContext.handle === DEFAULT_VOUCHER_HANDLE
  ) {
    return;
  }

  document.cookie = `${VOUCHER_CONTEXT_COOKIE_KEY}=${encodeURIComponent(
    JSON.stringify(normalizedContext),
  )}; path=/; max-age=${VOUCHER_COOKIE_MAX_AGE}; SameSite=Lax`;
}

function clearVoucherContextCookie() {
  document.cookie = `${VOUCHER_CONTEXT_COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

function clearStoredVoucherContext() {
  cachedVoucherHandle = "";
  cachedVoucherTag = "";
  cachedVoucherName = "";
  writeVoucherContextToSession(null);
  clearVoucherContextCookie();
}

function cacheVoucherContext(
  context,
  { persistSession = true, persistCookie = true } = {},
) {
  const normalizedContext = normalizeVoucherContext(context);

  cachedVoucherHandle = normalizedContext?.handle || "";
  cachedVoucherTag = normalizedContext?.tag || "";
  cachedVoucherName = normalizedContext?.voucherName || "";

  if (persistSession) {
    writeVoucherContextToSession(normalizedContext);
  }

  if (persistCookie && normalizedContext) {
    writeVoucherContextToCookie(normalizedContext);
  }

  return normalizedContext;
}

function publishVoucherContext(context) {
  const normalizedContext = normalizeVoucherContext(context);
  const activeTag = normalizedContext?.tag || "";

  if (window.Theme?.shopifyStorefront) {
    window.Theme.shopifyStorefront.activeVoucherContext = normalizedContext
      ? {
          handle: normalizedContext.handle,
          flTag: normalizedContext.tag,
          tag: normalizedContext.tag,
          voucherName: normalizedContext.voucherName,
          config: normalizedContext.config,
        }
      : null;
    window.Theme.shopifyStorefront.activeFreeGiftVoucherTag = activeTag;
  }

  window.dispatchEvent(
    new CustomEvent(VOUCHER_CONTEXT_READY_EVENT, {
      detail: {
        context: normalizedContext
          ? {
              handle: normalizedContext.handle,
              flTag: normalizedContext.tag,
              tag: normalizedContext.tag,
              voucherName: normalizedContext.voucherName,
              config: normalizedContext.config,
            }
          : null,
        flTag: activeTag,
        tag: activeTag,
      },
    }),
  );
}

/**
 * Reads the voucher metaobject handle from the current URL.
 * @returns {string}
 */
function getVoucherHandleFromURL() {
  try {
    const handle = String(
      new URLSearchParams(window.location.search).get("gc_campaign") || "",
    )
      .trim()
      .toLowerCase();

    if (handle) {
      logVoucherDebug("Using voucher handle from URL.", {
        handle,
        search: window.location.search,
      });
      return handle;
    }

    logVoucherDebug("No voucher handle found in URL.", {
      search: window.location.search,
    });

    return "";
  } catch (_error) {
    logVoucherDebug("Voucher handle URL parsing failed.", {
      search: window.location.search,
    });
    return "";
  }
}

/**
 * Resolves and caches the active voucher tag from the URL metaobject handle.
 * @returns {Promise<{handle: string, tag: string, voucherName: string}>}
 */
async function resolveVoucherTagFromURL() {
  const handle = getVoucherHandleFromURL();
  if (!handle) {
    const cookieContext = readVoucherContextFromCookie();
    if (cookieContext) {
      const normalizedCookieContext = cacheVoucherContext(cookieContext, {
        persistSession: true,
        persistCookie: true,
      });
      publishVoucherContext(normalizedCookieContext);
      logVoucherDebug("Using voucher context from cookie.", {
        handle: cachedVoucherHandle,
        tag: cachedVoucherTag,
        voucherName: cachedVoucherName,
        config: cookieContext.config || null,
      });
      return {
        handle: cachedVoucherHandle,
        tag: cachedVoucherTag,
        voucherName: cachedVoucherName,
      };
    }

    const defaultContext = buildVoucherContext(DEFAULT_VOUCHER_HANDLE);
    cachedVoucherHandle = "";
    cachedVoucherTag = defaultContext?.tag || "";
    cachedVoucherName = defaultContext?.voucherName || "";
    writeVoucherContextToSession(null);
    voucherTagRequest = null;
    logVoucherDebug(
      "Using default voucher entry because no handle was available.",
      {
        handle: DEFAULT_VOUCHER_HANDLE,
        tag: cachedVoucherTag,
        voucherName: cachedVoucherName,
        config: defaultContext?.config || null,
      },
    );
    return {
      handle: "",
      tag: cachedVoucherTag,
      voucherName: cachedVoucherName,
    };
  }

  if (handle === cachedVoucherHandle && cachedVoucherTag) {
    logVoucherDebug("Using cached voucher tag in memory.", {
      handle: cachedVoucherHandle,
      tag: cachedVoucherTag,
      voucherName: cachedVoucherName,
    });
    return {
      handle: cachedVoucherHandle,
      tag: cachedVoucherTag,
      voucherName: cachedVoucherName,
    };
  }

  const themeContext = buildVoucherContext(handle);
  if (themeContext) {
    const normalizedThemeContext = cacheVoucherContext(themeContext, {
      persistSession: true,
      persistCookie: true,
    });
    publishVoucherContext(normalizedThemeContext);
    return {
      handle: cachedVoucherHandle,
      tag: cachedVoucherTag,
      voucherName: cachedVoucherName,
    };
  }

  clearStoredVoucherContext();

  const defaultContext = buildVoucherContext(DEFAULT_VOUCHER_HANDLE);
  if (defaultContext) {
    cachedVoucherTag = defaultContext.tag;
    cachedVoucherName = defaultContext.voucherName;
    publishVoucherContext({
      handle: DEFAULT_VOUCHER_HANDLE,
      tag: defaultContext.tag,
      voucherName: defaultContext.voucherName,
      config: defaultContext.config || null,
    });
    logVoucherDebug(
      "Falling back to default voucher entry after invalid handle.",
      {
        handle,
        fallbackHandle: DEFAULT_VOUCHER_HANDLE,
        tag: cachedVoucherTag,
        voucherName: cachedVoucherName,
        config: defaultContext.config || null,
      },
    );
    return {
      handle: "",
      tag: cachedVoucherTag,
      voucherName: cachedVoucherName,
    };
  }

  cachedVoucherHandle = "";
  cachedVoucherTag = "";
  cachedVoucherName = "";
  voucherTagRequest = null;

  logVoucherDebug("No voucher entry matched the handle in the Theme map.", {
    handle,
    availableHandles: Object.keys(
      Theme?.shopifyStorefront?.voucherMetaobjectEntries || {},
    ),
  });

  return { handle: "", tag: "", voucherName: "" };
}

function initializeVoucherContext() {
  const handle = getVoucherHandleFromURL();

  logVoucherDebug("Initializing voucher context on page load.", {
    handle,
    search: window.location.search,
  });

  Promise.resolve()
    .then(() => resolveVoucherTagFromURL())
    .then(({ handle, tag, voucherName }) =>
      persistVoucherCartAttribute(tag, voucherName, {
        voucherHandle: handle,
      }),
    )
    .catch((error) => {
      console.error("Failed to initialize voucher context:", error);
    });
}

if (typeof window !== "undefined") {
  initializeVoucherContext();
}

/**
 * Normalizes a selling-plan name for comparisons.
 * @param {string | undefined | null} value
 * @returns {string}
 */
function normalizePlanName(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function getSelectedSellingPlanName(select) {
  if (!(select instanceof HTMLSelectElement)) return "";

  const selectedOption = select.selectedOptions[0];
  if (!(selectedOption instanceof HTMLOptionElement)) return "";

  return String(
    selectedOption.dataset.sellingPlanName || selectedOption.textContent || "",
  ).trim();
}

/**
 * Reads the active subscription cadence from the cart payload.
 * @param {Cart | null | undefined} cart
 * @returns {string}
 */
function getSubscriptionPlanNameFromCart(cart) {
  if (!Array.isArray(cart?.items)) return "";

  const subscriptionItem = cart.items.find((item) => {
    if (!item?.selling_plan_allocation?.selling_plan?.name) return false;

    return item?.properties?._gc_subscription_free_gift !== "true";
  });

  return String(
    subscriptionItem?.selling_plan_allocation?.selling_plan?.name || "",
  ).trim();
}

let cachedSubscriptionPlanName = "";
let hasLoadedSubscriptionPlanName = false;
let subscriptionPlanNameRequest = null;

/**
 * Persists the current cart subscription cadence in memory.
 * @param {string} value
 * @returns {string}
 */
function setCachedSubscriptionPlanName(value) {
  cachedSubscriptionPlanName = String(value || "").trim();
  hasLoadedSubscriptionPlanName = true;
  return cachedSubscriptionPlanName;
}

/**
 * Fetches the current cart cadence once and reuses it across product forms.
 * @param {{force?: boolean}} [options]
 * @returns {Promise<string>}
 */
async function fetchCachedSubscriptionPlanName(options = {}) {
  const { force = false } = options;

  if (subscriptionPlanNameRequest) {
    return subscriptionPlanNameRequest;
  }

  if (!force && hasLoadedSubscriptionPlanName) {
    return cachedSubscriptionPlanName;
  }

  // subscriptionPlanNameRequest = fetch("/cart.js")
    subscriptionPlanNameRequest = getCachedCart({ force })
    .then((cart) =>
      setCachedSubscriptionPlanName(getSubscriptionPlanNameFromCart(cart)),
    )
    .catch((error) => {
      console.error("Failed to fetch cart subscription cadence:", error);
      return cachedSubscriptionPlanName;
    })
    .finally(() => {
      subscriptionPlanNameRequest = null;
    });

  return subscriptionPlanNameRequest;
}

// async function fetchCartSnapshot() {
//   try {
//     const response = await fetch("/cart.js");
//     return await response.json();
//   } catch (error) {
//     console.error("Failed to fetch cart snapshot:", error);
//     return null;
//   }
// }

let cachedCart = null;
let cartFetchRequest = null;

/**
 * Fetches the cart from /cart.js, but caches the result in memory.
 * Subsequent calls will return the cached cart instantly unless `force` is true.
 */
async function getCachedCart(force = false) {
  if (!force && cachedCart) {
    return cachedCart;
  }
  if (cartFetchRequest) {
    return cartFetchRequest;
  }
  
  cartFetchRequest = fetch("/cart.js")
    .then((response) => response.json())
    .then((cart) => {
      cachedCart = cart;
      return cart;
    })
    .catch((error) => {
      console.error("Failed to fetch cart:", error);
      return null;
    })
    .finally(() => {
      cartFetchRequest = null;
    });
    
  return cartFetchRequest;
}

/**
 * Updates the in-memory cart cache.
 */
function setCachedCart(cart) {
  if (cart && typeof cart === "object" && Array.isArray(cart.items)) {
    cachedCart = cart;
  }
}

function isFreeGiftCartItem(item) {
  return item?.properties?._gc_subscription_free_gift === "true";
}

function isManagedRechargeBundleCartItem(item) {
  return (
    Boolean(item?.properties?._rc_bundle) ||
    String(item?.properties?._rc_bundle_parent || "").trim() ===
      "goodchop-subscription-box"
  );
}

function isSubscribedCartItem(item) {
  return (
    Boolean(item?.selling_plan_allocation) ||
    isManagedRechargeBundleCartItem(item)
  );
}

function getSubscribedCartItems(cart) {
  return Array.isArray(cart?.items)
    ? cart.items.filter((item) => isSubscribedCartItem(item))
    : [];
}

function getSubscriptionCoreItems(cart) {
  return getSubscribedCartItems(cart).filter(
    (item) => !isFreeGiftCartItem(item),
  );
}

function getCurrentFreeGiftCartItem(cart) {
  return (
    getSubscribedCartItems(cart).find((item) => isFreeGiftCartItem(item)) ||
    null
  );
}

function getCartItemSellingPlanId(item) {
  return String(
    item?.selling_plan_allocation?.selling_plan?.id ||
      item?.selling_plan_allocation?.selling_plan_id ||
      item?.selling_plan_allocation?.id ||
      "",
  ).trim();
}

function getCartItemSellingPlanName(item) {
  return String(
    item?.selling_plan_allocation?.selling_plan?.name ||
      item?.selling_plan_allocation?.name ||
      "",
  ).trim();
}

function getSelectionCollectionId(selection) {
  const directCollectionId = String(selection?.collectionId || "").trim();
  if (directCollectionId) return directCollectionId;

  return parseCollectionIds(selection?.collectionIds)[0] || "";
}

function getBundleSelectionMergeKey(selection) {
  const productId = String(selection?.productId || "").trim();
  const variantId = String(selection?.variantId || "").trim();
  const collectionId = getSelectionCollectionId(selection);

  if (!productId || !variantId || !collectionId) return "";

  return [
    selection?.isFreeGift === true ? "gift" : "core",
    productId,
    variantId,
    collectionId,
  ].join("::");
}

function mergeBundleSelections(selections) {
  const mergedSelections = new Map();

  selections.forEach((selection) => {
    if (!selection) return;

    const mergeKey = getBundleSelectionMergeKey(selection);
    if (!mergeKey) return;

    const existingSelection = mergedSelections.get(mergeKey);
    if (existingSelection) {
      existingSelection.quantity =
        Number(existingSelection.quantity || 0) +
        Number(selection.quantity || 0);
      return;
    }

    mergedSelections.set(mergeKey, {
      ...selection,
      quantity: Number(selection.quantity || 0),
    });
  });

  return Array.from(mergedSelections.values());
}

function buildSelectionFromCartItem(item, fallbackSellingPlanId = "") {
  const productId = String(item?.product_id || "").trim();
  const variantId = String(item?.variant_id || "").trim();
  const quantity = Number(item?.quantity || 0);
  const sellingPlan =
    getCartItemSellingPlanId(item) ||
    String(normalizeSellingPlanId(fallbackSellingPlanId) || "").trim();
  const properties =
    item && typeof item.properties === "object" && item.properties
      ? item.properties
      : {};
  const collectionIds = parseCollectionIds(
    properties._gc_product_collection_ids ||
      properties._rc_bundle_collection_id ||
      [],
  );
  const collectionId = String(
    properties._rc_bundle_collection_id || collectionIds[0] || "",
  ).trim();

  if (!productId || !variantId || !quantity || !sellingPlan || !collectionId) {
    return null;
  }

  return {
    productId,
    variantId,
    quantity,
    sellingPlan,
    collectionId,
    collectionIds,
    isFreeGift: isFreeGiftCartItem(item),
  };
}

function cartHasFreeGiftItem(cart) {
  return Boolean(getCurrentFreeGiftCartItem(cart));
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
      );
    }
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Shows the in-flight loader state on the add to cart button.
   */
  startLoading() {
    this.#setBusyState("adding");
  }

  /**
   * Shows the redirecting state on the add to cart button.
   */
  startRedirecting() {
    this.#setBusyState("redirecting");
  }

  /**
   * Hides the in-flight loader state on the add to cart button.
   */
  stopLoading() {
    this.refs.addToCartButton.removeAttribute("data-loading-state");
    this.refs.addToCartButton.removeAttribute("aria-busy");
    this.#resetLoadingText();
    this.enable();
  }

  /**
   * @param {"adding" | "redirecting"} state
   */
  #setBusyState(state) {
    const loadingText = this.#getLoadingTextElement();
    const nextText =
      state === "redirecting"
        ? loadingText?.dataset.redirectingLabel || "Redirecting..."
        : loadingText?.dataset.addingLabel || "Adding...";

    if (loadingText) {
      loadingText.textContent = nextText;
    }

    this.refs.addToCartButton.dataset.loadingState = state;
    this.refs.addToCartButton.setAttribute("aria-busy", "true");
    this.disable();
  }

  #resetLoadingText() {
    const loadingText = this.#getLoadingTextElement();
    if (!loadingText) return;

    loadingText.textContent = loadingText.dataset.addingLabel || "Adding...";
  }

  #getLoadingTextElement() {
    const loadingText = this.refs.addToCartButton.querySelector(
      ".add-to-cart__loading-text",
    );

    return loadingText instanceof HTMLElement ? loadingText : null;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest("product-form-component")
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        event.preventDefault();
        quantitySelector.showQuantityLimitError?.(validation.maxQuantity);
        return;
      }
    }
    quantitySelector?.hideQuantityLimitError?.();
    const sellingPlanSelect = productForm?.querySelector(
      "[data-product-selling-plan]",
    );
    const cachedLoginState = getCachedAffinityCustomerLoggedInState();
    const buttonShowsAddToBox = isAddToCartButtonShowingAddToBox(
      this.refs.addToCartButton,
    );
    const buttonInBoxMode =
      this.refs.addToCartButton.dataset.affinityPurchaseMode === "box";
    const hasPdpSubscriptionWidget =
      isPdpSubscriptionWidgetEnabled(productForm);
    const pdpManualPurchaseMode = getPdpManualPurchaseModeOverride(productForm);

    if (
      sellingPlanSelect instanceof HTMLSelectElement &&
      cachedLoginState !== true &&
      (buttonInBoxMode || buttonShowsAddToBox)
    ) {
      const guestPurchasePreference = getAffinityGuestPurchasePreference();

      if (hasPdpSubscriptionWidget && pdpManualPurchaseMode === "one-time") {
        return;
      }

      if (
        hasPdpSubscriptionWidget &&
        pdpManualPurchaseMode === "subscription"
      ) {
        return;
      }

      if (guestPurchasePreference === "subscription") {
        return;
      }

      if (guestPurchasePreference === "one-time") {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        submitGuestPurchaseChoice(
          productForm,
          this.refs.addToCartButton,
          "one-time",
        );
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      openLoginModal({
        onContinueAsNewBox: () => {
          if (!(productForm instanceof HTMLElement)) return;

          submitGuestPurchaseChoice(
            productForm,
            this.refs.addToCartButton,
            "subscription",
          );
        },
      });
      return;
    }

    const isProductCardToggleFlow = Boolean(
      productForm?.closest("[data-product-card-cart-toggle]"),
    );

    if (
      this.refs.addToCartButton.dataset.puppet !== "true" &&
      this.refs.addToCartButton.dataset.affinityPurchaseMode !== "box" &&
      !isProductCardToggleFlow
    ) {
      const animationEnabled = this.dataset.addToCartAnimation === "true";
      if (animationEnabled && !event.target.closest(".quick-add-modal")) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    let flyToCartClass = addToCartButton.classList.contains("quick-add__button")
      ? "fly-to-cart--quick"
      : "fly-to-cart--main";

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== "true") {
      addToCartButton.dataset.added = "true";
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute("data-added");

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal,
    });

    this.addEventListener("change", this.#onChange, { signal });
    this.addEventListener("click", this.#onClick, { signal });
    window.addEventListener(
      VOUCHER_CONTEXT_READY_EVENT,
      this.#onVoucherContextReady,
      {
        signal,
      },
    );

    if (this.#isSubscriptionAwareForm()) {
      void bootstrapAffinitySubscriptionPresence().finally(() => {
        void this.#syncAffinityPurchaseOptionState();
        syncPdpSubscriptionWidget(this);
      });
    }

    void this.#syncSellingPlanLock();
    void this.#syncAffinityPurchaseOptionState();
    syncPdpSubscriptionWidget(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartQty = cart.items.reduce((total, item) => {
      if (item?.variant_id?.toString() !== variantIdInput.value.toString()) {
        return total;
      }

      return total + Number(item.quantity || 0);
    }, 0);

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector("quantity-selector-component")
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return 0;

    try {
      // const response = await fetch("/cart.js");
      // const cart = await response.json();
      const cart = await getCachedCart();
      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    const isProductFormCartUpdate =
      event.detail?.data?.source === "product-form-component";
    const sellingPlanId = normalizeSellingPlanId(
      event.detail?.data?.sellingPlanId || "",
    );
    const shouldSkipQuantitySync =
      event.detail?.sourceId === this.id || isProductFormCartUpdate;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      setCachedCart(cart);
    } else {
      cachedCart = null; // Invalidate if event doesn't contain cart data
    }
    if (!shouldSkipQuantitySync) {
      if (cart?.items) {
        this.#updateCartQuantityFromData(cart);
      } else {
        await this.#fetchAndUpdateCartQuantity();
      }
    }

    if (cart?.items) {
      this.#applySellingPlanLock(
        setCachedSubscriptionPlanName(getSubscriptionPlanNameFromCart(cart)),
      );
    } else if (!(isProductFormCartUpdate && !sellingPlanId)) {
      await this.#syncSellingPlanLock({ force: true });
    }
  };

  /**
   * Gets the current selling-plan selector for this form.
   * @returns {HTMLSelectElement | null}
   */
  #getSellingPlanSelect() {
    const select = this.querySelector("[data-product-selling-plan]");
    return select instanceof HTMLSelectElement ? select : null;
  }

  #isProductCardForm() {
    return Boolean(this.closest("[data-product-card-cart-toggle]"));
  }

  #isSubscriptionAwareForm() {
    return this.#getSellingPlanSelect() instanceof HTMLSelectElement;
  }

  #getAddToCartButton() {
    const button =
      this.refs?.addToCartButtonContainer?.refs?.addToCartButton ?? null;
    return button instanceof HTMLButtonElement ? button : null;
  }

  async #syncAffinityPurchaseOptionState() {
    if (!this.#isSubscriptionAwareForm()) return;

    const addToCartButton = this.#getAddToCartButton();
    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    const sellingPlanSelect = this.#getSellingPlanSelect();
    const selectedSellingPlanId = normalizeSellingPlanId(
      sellingPlanSelect?.value || "",
    );
    const defaultLabel =
      addToCartButton.dataset.affinityDefaultLabel ||
      getAddToCartButtonTextElement(addToCartButton)?.textContent?.trim() ||
      "";
    const addToBoxLabel = getAffinityAddToBoxLabel(this);
    const hasPdpSubscriptionWidget = isPdpSubscriptionWidgetEnabled(this);
    const pdpPurchaseMode = hasPdpSubscriptionWidget
      ? getPdpEffectivePurchaseMode(this)
      : "";

    if (!addToCartButton.dataset.affinityDefaultLabel && defaultLabel) {
      addToCartButton.dataset.affinityDefaultLabel = defaultLabel;
    }

    if (addToCartButton.disabled) {
      addToCartButton.dataset.affinityPurchaseMode = "cart";
      syncPdpSubscriptionWidget(this);
      return;
    }

    const customerIsLoggedIn = getCachedAffinityCustomerLoggedInState();
    const guestPurchasePreference = getAffinityGuestPurchasePreference();
    const hasSelectedSellingPlan = Boolean(selectedSellingPlanId);

    const knownSubscriptionPresence =
      customerIsLoggedIn === true
        ? getKnownAffinitySubscriptionPresence()
        : false;
    const hasRechargeSubscriptions = knownSubscriptionPresence === true;

    if (customerIsLoggedIn === true && knownSubscriptionPresence === null) {
      void bootstrapAffinitySubscriptionPresence().finally(() => {
        void this.#syncAffinityPurchaseOptionState();
      });
    }

    if (hasPdpSubscriptionWidget && pdpPurchaseMode === "one-time") {
      addToCartButton.dataset.affinityPurchaseMode = "cart";

      if (addToCartButton.dataset.affinityDefaultLabel) {
        setAddToCartButtonText(
          addToCartButton,
          addToCartButton.dataset.affinityDefaultLabel,
        );
      }

      syncPdpSubscriptionWidget(this);
      return;
    }

    if (hasRechargeSubscriptions) {
      addToCartButton.dataset.affinityPurchaseMode = "box";
      setAddToCartButtonText(addToCartButton, addToBoxLabel);
      syncPdpSubscriptionWidget(this);
      return;
    }

    if (
      hasPdpSubscriptionWidget &&
      pdpPurchaseMode === "subscription" &&
      hasSelectedSellingPlan
    ) {
      addToCartButton.dataset.affinityPurchaseMode =
        customerIsLoggedIn === true ? "cart" : "box";
      setAddToCartButtonText(addToCartButton, addToBoxLabel);
      syncPdpSubscriptionWidget(this);
      return;
    }

    if (customerIsLoggedIn !== true && guestPurchasePreference === "one-time") {
      addToCartButton.dataset.affinityPurchaseMode = "cart";

      if (addToCartButton.dataset.affinityDefaultLabel) {
        setAddToCartButtonText(
          addToCartButton,
          addToCartButton.dataset.affinityDefaultLabel,
        );
      }
      syncPdpSubscriptionWidget(this);
      return;
    }

    if (hasSelectedSellingPlan) {
      addToCartButton.dataset.affinityPurchaseMode =
        customerIsLoggedIn === true ? "cart" : "box";
      setAddToCartButtonText(addToCartButton, addToBoxLabel);
      syncPdpSubscriptionWidget(this);
      return;
    }

    logAffinitySubscriptionDebug("Synced product card purchase mode.", {
      productId: this.dataset.productId || null,
      customerIsLoggedIn,
      liquidCustomerId: getAffinityRechargeConfig().customerId || null,
      liquidCustomerEmail: getAffinityRechargeConfig().customerEmail || null,
      storefrontHost: getAffinityRechargeConfig().storefrontHost || null,
      selectedSellingPlanId: selectedSellingPlanId || "",
      knownSubscriptionPresence,
      hasRechargeSubscriptions,
    });

    addToCartButton.dataset.affinityPurchaseMode = "cart";

    if (addToCartButton.dataset.affinityDefaultLabel) {
      setAddToCartButtonText(
        addToCartButton,
        addToCartButton.dataset.affinityDefaultLabel,
      );
    }

    syncPdpSubscriptionWidget(this);
  }

  #onChange = (event) => {
    if (
      event.target instanceof HTMLSelectElement &&
      event.target.matches("[data-product-selling-plan]")
    ) {
      void this.#syncAffinityPurchaseOptionState();
      syncPdpSubscriptionWidget(this);
    }
  };

  #onClick = (event) => {
    if (!isPdpSubscriptionWidgetEnabled(this)) return;

    const optionCard =
      event.target instanceof Element
        ? event.target.closest("[data-pdp-option-card]")
        : null;

    if (
      !(optionCard instanceof HTMLButtonElement) ||
      !this.contains(optionCard)
    ) {
      return;
    }

    event.preventDefault();

    setPdpPurchaseMode(this, optionCard.dataset.pdpOptionCard);
  };

  #onVoucherContextReady = () => {
    syncPdpSubscriptionWidget(this);
  };

  /**
   * Applies the current cart cadence lock to this form's selling-plan selector.
   * One-time remains available, while mismatched subscription cadences are disabled.
   * @param {string} lockedPlanName
   */
  #applySellingPlanLock(lockedPlanName = "") {
    const select = this.#getSellingPlanSelect();
    if (!(select instanceof HTMLSelectElement)) return;

    const normalizedLockedPlanName = normalizePlanName(lockedPlanName);
    let firstMatchingOptionValue = "";

    Array.from(select.options).forEach((option) => {
      if (option.value === "") {
        option.disabled = false;
        option.removeAttribute("title");
        return;
      }

      const normalizedOptionPlanName = normalizePlanName(
        option.dataset.sellingPlanName || option.textContent,
      );
      const matchesLockedPlan =
        !normalizedLockedPlanName ||
        normalizedOptionPlanName === normalizedLockedPlanName;

      option.disabled = !matchesLockedPlan;

      if (matchesLockedPlan && !firstMatchingOptionValue) {
        firstMatchingOptionValue = option.value;
      }

      if (!matchesLockedPlan && lockedPlanName) {
        option.title = `Unavailable while your cart frequency is ${lockedPlanName}.`;
      } else {
        option.removeAttribute("title");
      }
    });

    const selectedOption = select.options[select.selectedIndex];
    if (selectedOption?.disabled) {
      select.value = firstMatchingOptionValue || "";
    }

    if (lockedPlanName) {
      select.dataset.lockedPlanName = lockedPlanName;
    } else {
      delete select.dataset.lockedPlanName;
    }

    void this.#syncAffinityPurchaseOptionState();
  }

  /**
   * Syncs the current cart cadence lock for this form.
   * @param {{force?: boolean, lockName?: string}} [options]
   */
  async #syncSellingPlanLock(options = {}) {
    const { force = false, lockName } = options;
    const nextLockName =
      typeof lockName === "string"
        ? setCachedSubscriptionPlanName(lockName)
        : await fetchCachedSubscriptionPlanName({ force });

    this.#applySellingPlanLock(nextLockName);
  }

  async #maybeHandleAffinityAddToBoxIntent({ selectedSellingPlanId }) {
    if (!this.#isSubscriptionAwareForm()) {
      return false;
    }

    const guestPurchaseModeBypass = consumeGuestPurchaseModeBypass(this);

    if (guestPurchaseModeBypass) {
      logAffinitySubscriptionDebug("Continuing with guest purchase choice.", {
        productId: this.dataset.productId || null,
        guestPurchaseModeBypass,
        selectedSellingPlanId: selectedSellingPlanId || "",
      });
      return false;
    }

    const addToCartButton = this.#getAddToCartButton();
    const isButtonInBoxMode =
      addToCartButton instanceof HTMLButtonElement &&
      addToCartButton.dataset.affinityPurchaseMode === "box";
    const isButtonShowingAddToBox =
      addToCartButton instanceof HTMLButtonElement &&
      isAddToCartButtonShowingAddToBox(addToCartButton);
    const hasSelectedSellingPlan = Boolean(selectedSellingPlanId);
    const cachedLoginState = getCachedAffinityCustomerLoggedInState();
    const guestPurchasePreference = getAffinityGuestPurchasePreference();
    const hasPdpSubscriptionWidget = isPdpSubscriptionWidgetEnabled(this);
    const pdpManualPurchaseMode = getPdpManualPurchaseModeOverride(this);

    if (hasPdpSubscriptionWidget && pdpManualPurchaseMode === "one-time") {
      return false;
    }

    if (hasPdpSubscriptionWidget && pdpManualPurchaseMode === "subscription") {
      return false;
    }

    if (
      cachedLoginState !== true &&
      guestPurchasePreference === "subscription"
    ) {
      logAffinitySubscriptionDebug(
        "Using saved guest new box preference for cart flow.",
        {
          productId: this.dataset.productId || null,
          selectedSellingPlanId: selectedSellingPlanId || "",
        },
      );
      return false;
    }

    if (cachedLoginState !== true && guestPurchasePreference === "one-time") {
      logAffinitySubscriptionDebug(
        "Using saved guest one-time preference for cart flow.",
        {
          productId: this.dataset.productId || null,
          selectedSellingPlanId: selectedSellingPlanId || "",
        },
      );
      return false;
    }

    if (cachedLoginState !== true) {
      logAffinitySubscriptionDebug(
        "Opening login modal for logged-out customer.",
        {
          productId: this.dataset.productId || null,
          cachedLoginState,
          selectedSellingPlanId: selectedSellingPlanId || "",
          isButtonInBoxMode,
        },
      );
      openLoginModal({
        onContinueAsNewBox: () => {
          submitGuestPurchaseChoice(this, addToCartButton, "subscription");
        },
      });
      return true;
    }

    const cachedSubscriptionState = getKnownAffinitySubscriptionPresence();

    if (cachedSubscriptionState === false) {
      logAffinitySubscriptionDebug(
        "Skipping Recharge redirect for cart-mode click.",
        {
          productId: this.dataset.productId || null,
          selectedSellingPlanId: selectedSellingPlanId || "",
          cachedSubscriptionState,
          isButtonInBoxMode,
          isButtonShowingAddToBox,
        },
      );
      return false;
    }

    const hasRechargeSubscriptions =
      cachedSubscriptionState === null
        ? await bootstrapAffinitySubscriptionPresence()
        : cachedSubscriptionState;
    const customerIsLoggedIn = hasRechargeSubscriptions
      ? true
      : await resolveAffinityCustomerLoggedIn();

    logAffinitySubscriptionDebug("Evaluating Add to Box intent.", {
      productId: this.dataset.productId || null,
      cachedLoginState,
      customerIsLoggedIn,
      liquidCustomerId: getAffinityRechargeConfig().customerId || null,
      liquidCustomerEmail: getAffinityRechargeConfig().customerEmail || null,
      storefrontHost: getAffinityRechargeConfig().storefrontHost || null,
      hasSellingPlanSelector: true,
      selectedSellingPlanId: selectedSellingPlanId || "",
      isButtonInBoxMode,
      isButtonShowingAddToBox,
      cachedSubscriptionState,
      hasRechargeSubscriptions,
    });

    if (hasRechargeSubscriptions) {
      const addToCartContainers = Array.from(
        this.querySelectorAll("add-to-cart-component"),
      ).filter(
        (container) =>
          container instanceof AddToCartComponent &&
          container.refs?.addToCartButton instanceof HTMLButtonElement,
      );

      addToCartContainers.forEach((container) => {
        container.startRedirecting?.();
      });

      const portalDestination = await resolveAffinityPortalDestination();

      logAffinitySubscriptionDebug(
        "Redirecting subscribed customer to portal.",
        {
          productId: this.dataset.productId || null,
          shouldOpenSubscriptions: portalDestination.shouldOpenSubscriptions,
        },
      );

      if (portalDestination.shouldOpenSubscriptions) {
        persistAffinityPortalSubscriptionsIntent();
        window.location.href = buildAffinityPortalUrl({
          openSubscriptions: true,
        });
        return true;
      }

      persistAffinityPortalBundleEditorIntent();
      window.location.href = buildAffinityPortalUrl({
        autoOpenBundleEditor: true,
      });
      return true;
    }

    logAffinitySubscriptionDebug(
      "Falling through to normal add-to-cart flow.",
      {
        productId: this.dataset.productId || null,
        customerIsLoggedIn,
        hasRechargeSubscriptions,
        selectedSellingPlanId: selectedSellingPlanId || "",
        isButtonInBoxMode,
      },
    );

    return false;
  }

  /**
   * Returns true when another component already syncs visible cart state after
   * add-to-cart, so local quantity refresh can happen in the background.
   * @returns {boolean}
   */
  #shouldDeferCartQuantityRefresh() {
    return Boolean(
      this.closest("[data-product-card-cart-toggle], quick-add-component"),
    );
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
 async handleSubmit(event) {
  const { addToCartTextError } = this.refs;
  // Stop default behaviour from the browser
  event.preventDefault();

  if (this.#timeout) clearTimeout(this.#timeout);

  // Query for ALL add-to-cart components
  const allAddToCartContainers =
    /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll("add-to-cart-component")
    );

  // Check if ANY add to cart button is disabled and do an early return if it is
  const anyButtonDisabled = Array.from(allAddToCartContainers).some(
    (container) => container.refs?.addToCartButton?.disabled,
  );
  if (anyButtonDisabled) return;

  const loadingButtonContainers = Array.from(allAddToCartContainers).filter(
    (container) =>
      container.refs?.addToCartButton instanceof HTMLButtonElement,
  );
  const setLoadingState = (isLoading) => {
    loadingButtonContainers.forEach((container) => {
      if (isLoading) {
        container.startLoading?.();
      } else {
        container.stopLoading?.();
      }
    });
  };

  // Send the add to cart information to the cart
  const form = this.querySelector("form");

  if (!form) throw new Error("Product form element missing");

  const customerIsLoggedIn = getCachedAffinityCustomerLoggedInState();
  const guestPurchasePreference = getAffinityGuestPurchasePreference();
  const sellingPlanSelect = this.#getSellingPlanSelect();
  const pdpManualPurchaseMode = getPdpManualPurchaseModeOverride(this);

  if (
    sellingPlanSelect instanceof HTMLSelectElement &&
    pdpManualPurchaseMode === "subscription" &&
    !sellingPlanSelect.value
  ) {
    sellingPlanSelect.value =
      getDefaultSubscriptionSellingPlanValue(sellingPlanSelect);
    sellingPlanSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  if (
    customerIsLoggedIn !== true &&
    (pdpManualPurchaseMode === "one-time" ||
      (pdpManualPurchaseMode !== "subscription" &&
        guestPurchasePreference === "one-time")) &&
    sellingPlanSelect instanceof HTMLSelectElement &&
    sellingPlanSelect.value
  ) {
    sellingPlanSelect.value = "";
    sellingPlanSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  if (this.refs.quantitySelector?.canAddToCart) {
    const validation = this.refs.quantitySelector.canAddToCart();

    if (!validation.canAdd) {
      // Disable ALL add-to-cart buttons
      for (const container of allAddToCartContainers) {
        container.disable();
      }

      const errorTemplate = this.dataset.quantityErrorMax || "";
      const errorMessage = errorTemplate.replace(
        "{{ maximum }}",
        validation.maxQuantity?.toString() || "",
      );
      if (addToCartTextError) {
        addToCartTextError.classList.remove("hidden");

        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          const newTextNode = document.createTextNode(errorMessage);
          addToCartTextError.appendChild(newTextNode);
        }

        this.#setLiveRegionText(errorMessage);

        if (this.#timeout) clearTimeout(this.#timeout);
        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add("hidden");
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }

      setTimeout(() => {
        // Re-enable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.enable();
        }
      }, ERROR_BUTTON_REENABLE_DELAY);

      return;
    }
  }

  const formData = new FormData(form);
  const submittedVariantId = formData.get("id");
  const submittedQuantity =
    Number(formData.get("quantity")) || Number(this.dataset.quantityDefault);
  const selectedSellingPlanId = normalizeSellingPlanId(
    formData.get("selling_plan"),
  );
  const selectedSellingPlanName = getSelectedSellingPlanName(
    this.#getSellingPlanSelect(),
  );
  const productCollectionIds = parseCollectionIds(
    this.dataset.productCollections,
  );
  const shouldDeferLocalCartQuantityRefresh =
    this.#shouldDeferCartQuantityRefresh();

  if (
    await this.#maybeHandleAffinityAddToBoxIntent({
      selectedSellingPlanId,
    })
  ) {
    return;
  }

  setLoadingState(true);

  const cartItemsComponents = document.querySelectorAll(
    "cart-items-component",
  );
  const cartItemComponentsSectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.push(item.dataset.sectionId);
    }
  });

  const sections = cartItemComponentsSectionIds.join(",");
  const shouldRequestSections = Boolean(sections);
  const sectionsPayload = shouldRequestSections
    ? {
        sections,
        sections_url: window.location.pathname,
      }
    : {};
  if (shouldRequestSections) {
    formData.append("sections", sections);
  }

  if (selectedSellingPlanId) {
    try {
      await serializeRechargeBundleCartMutation(async () => {
        markAffinitySubscriptionCheckoutPending();

        // SPEED OPTIMIZATION: Resolve voucher configurations and take cart snapshots concurrently
        // const [voucherContext, cartSnapshot] = await Promise.all([
        //   resolveVoucherTagFromURL(),
        //   fetchCartSnapshot()
        // ]);

        const [voucherContext, cartSnapshot] = await Promise.all([
          resolveVoucherTagFromURL(),
          getCachedCart() // Instantly resolves if already cached!
        ]);

        const resolvedVoucherTag = String(voucherContext.tag || "").trim();
        const resolvedVoucherName = String(voucherContext.voucherName || "").trim();
        const voucherHandle = voucherContext.handle;

        logVoucherDebug("Voucher context resolved before cart add.", {
          voucherHandle,
          voucherTag: resolvedVoucherTag,
          voucherName: resolvedVoucherName,
        });

        const subscriptionVoucherDiscountCodes =
          getVoucherDiscountCodesForHandle(voucherHandle);

        const nextSelection = {
          productId: this.dataset.productId,
          variantId: submittedVariantId,
          quantity: submittedQuantity,
          sellingPlan: selectedSellingPlanId,
          collectionIds: productCollectionIds,
        };

        const subscribedCartItems = getSubscribedCartItems(cartSnapshot);
        const previousSubscriptionBundleIds =
          getRechargeBundleGroupIds(subscribedCartItems);
        const subscriptionCoreItems = getSubscriptionCoreItems(cartSnapshot);
        const rawSubscriptionCoreSelections = subscriptionCoreItems
          .map((item) =>
            buildSelectionFromCartItem(item, selectedSellingPlanId),
          )
          .filter(Boolean);
        const subscriptionCoreSelections = mergeBundleSelections(
          rawSubscriptionCoreSelections,
        );
        const currentFreeGiftItem = getCurrentFreeGiftCartItem(cartSnapshot);
        const currentFreeGiftSelection = currentFreeGiftItem
          ? buildSelectionFromCartItem(
              currentFreeGiftItem,
              selectedSellingPlanId,
            )
          : null;
        const hasIncompleteSubscriptionBundleData =
          subscribedCartItems.length > 0 &&
          (rawSubscriptionCoreSelections.length !==
            subscriptionCoreItems.length ||
            (currentFreeGiftItem && !currentFreeGiftSelection));

        if (hasIncompleteSubscriptionBundleData) {
          throw new Error(
            "Your subscription cart is still updating. Please try again in a moment.",
          );
        }

        const canRebuildExistingSubscriptionBundle =
          subscribedCartItems.length > 0 &&
          subscriptionCoreSelections.length > 0;
        const nextSelections = canRebuildExistingSubscriptionBundle
          ? subscriptionCoreSelections.map((selection) => ({ ...selection }))
          : [{ ...nextSelection }];

        if (canRebuildExistingSubscriptionBundle) {
          const nextSelectionCollectionId =
            getSelectionCollectionId(nextSelection);
          const existingSelectionIndex = nextSelections.findIndex(
            (selection) =>
              selection?.isFreeGift !== true &&
              String(selection?.productId || "").trim() ===
                String(nextSelection.productId || "").trim() &&
              String(selection?.variantId || "").trim() ===
                String(nextSelection.variantId || "").trim() &&
              getSelectionCollectionId(selection) ===
                nextSelectionCollectionId,
          );

          if (existingSelectionIndex >= 0) {
            nextSelections[existingSelectionIndex] = {
              ...nextSelections[existingSelectionIndex],
              quantity:
                Number(
                  nextSelections[existingSelectionIndex]?.quantity || 0,
                ) + submittedQuantity,
            };
          } else {
            nextSelections.push({ ...nextSelection });
          }

          if (currentFreeGiftSelection) {
            nextSelections.push(currentFreeGiftSelection);
          }
        }

        if (!cartHasFreeGiftItem(cartSnapshot) && !currentFreeGiftSelection) {
          const planSourceItem =
            subscriptionCoreItems[0] || currentFreeGiftItem;
          const freeGiftSelection = getPreferredFreeGiftSelection({
            sellingPlanId: String(
              getCartItemSellingPlanId(planSourceItem) ||
                selectedSellingPlanId,
            ),
            sellingPlanName:
              getCartItemSellingPlanName(planSourceItem) ||
              selectedSellingPlanName,
          });

          if (freeGiftSelection) {
            nextSelections.push(freeGiftSelection);
          }
        }

        const cartItems =
          await getRechargeDynamicBundleCartItems(nextSelections);
        const nextSubscriptionBundleIds =
          getRechargeBundleGroupIds(cartItems);
        const fetchCfg = fetchConfig("json", {
          body: JSON.stringify({
            items: cartItems,
            ...(previousSubscriptionBundleIds.length ? {} : sectionsPayload),
          }),
        });
        const requestHeaders = fetchCfg.headers;

        let activeResponse = await fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: requestHeaders,
        });
        let cartResponse = await activeResponse.json();

        if (
          activeResponse.ok &&
          !cartResponse.status &&
          !cartResponse.errors &&
          previousSubscriptionBundleIds.length
        ) {
           const refreshedCartSnapshot = await getCachedCart(true);
          setCachedCart(refreshedCartSnapshot); // Update cache again
          
          const refreshedSubscriptionLineKeysToRemove =
            getRechargeBundleLineKeysToRemove(
              refreshedCartSnapshot?.items || [],
              previousSubscriptionBundleIds,
              nextSubscriptionBundleIds,
            );

          if (refreshedSubscriptionLineKeysToRemove.length) {
            const updates = {};

            refreshedSubscriptionLineKeysToRemove.forEach((key) => {
              updates[key] = 0;
            });

            activeResponse = await fetch(
              Theme.routes.cart_update_url,
              fetchConfig("json", {
                body: JSON.stringify({
                  updates,
                  ...sectionsPayload,
                }),
              }),
            );
            cartResponse = await activeResponse.json();
          }
        }

        if (
          cartResponse.status ||
          cartResponse.errors ||
          !activeResponse.ok
        ) {
          const noCartDrawer = true;
          const errorMessage = String(
            cartResponse.message ||
              cartResponse.description ||
              "Unable to update cart.",
          );

          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              errorMessage,
              cartResponse.description,
              cartResponse.errors,
            ),
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove("hidden");

            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = errorMessage;
            } else {
              const newTextNode = document.createTextNode(errorMessage);
              addToCartTextError.appendChild(newTextNode);
            }
          }

          this.#setLiveRegionText(errorMessage);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount: submittedQuantity,
              productId: this.dataset.productId,
              sellingPlanId: selectedSellingPlanId || "",
              noCartDrawer,
            }),
          );

          return;
        }

        const id = submittedVariantId;
        if (!id) throw new Error("Form ID is required");

        if (addToCartTextError) {
          addToCartTextError.classList.add("hidden");
          addToCartTextError.removeAttribute("aria-live");
        }

        if (
          (resolvedVoucherTag || resolvedVoucherName) &&
          subscriptionVoucherDiscountCodes.length
        ) {
          void persistVoucherCartAttribute(
            resolvedVoucherTag,
            resolvedVoucherName,
            {
              discountCodes: subscriptionVoucherDiscountCodes,
              voucherHandle,
              sectionsPayload,
              force: true,
              returnResponse: true,
            },
          )
            .then((discountUpdateResponse) => {
              if (
                !discountUpdateResponse ||
                typeof discountUpdateResponse !== "object"
              ) {
                return;
              }

              document.dispatchEvent(
                new DiscountUpdateEvent(discountUpdateResponse, this.id),
              );
            })
            .catch((error) => {
              console.error("Failed to apply voucher discount codes.", error);
            });
        }

        const anyAddToCartButton =
          allAddToCartContainers[0]?.refs?.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector(
            ".add-to-cart-text--added",
          );
          const addedText =
            addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: "product-form-component",
            itemCount: submittedQuantity,
            productId: this.dataset.productId,
            sellingPlanId: selectedSellingPlanId || "",
            sections: cartResponse.sections,
            noCartDrawer: this.dataset.noCartDrawer === "true",
          }),
        );

        if (shouldDeferLocalCartQuantityRefresh) {
          return;
        }

        void this.#fetchAndUpdateCartQuantity();
      });
    } catch (error) {
      console.error(error);

      const message =
        error instanceof Error
          ? error.message
          : "Unable to add subscription bundle to cart.";

      if (addToCartTextError) {
        addToCartTextError.classList.remove("hidden");

        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = message;
        } else {
          addToCartTextError.appendChild(document.createTextNode(message));
        }
      }

      this.#setLiveRegionText(message);
    } finally {
      setLoadingState(false);
      cartPerformance.measureFromEvent("add:user-action", event);
    }

    return;
  }

  // RELOAD FIX: Configured for JSON mutations to ensure proper response parsing context
  const fetchCfg = fetchConfig("json", { body: formData });
  const requestHeaders = {
    ...fetchCfg.headers,
    Accept: "application/json",
  };
  const subscriptionLineKeysToRemove = [];
  const resolvedVoucherTag = "";
  const resolvedVoucherName = "";
  const subscriptionVoucherDiscountCodes = [];

  fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: requestHeaders,
  })
    .then(async (response) => {
      let activeResponse = response;
      let cartResponse = await response.json();
     setCachedCart(cartResponse);
      if (
        activeResponse.ok &&
        !cartResponse.status &&
        !cartResponse.errors &&
        subscriptionLineKeysToRemove.length
      ) {
        const updates = {};

        subscriptionLineKeysToRemove.forEach((key) => {
          updates[key] = 0;
        });

        activeResponse = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig("json", {
            body: JSON.stringify({
              updates,
              ...sectionsPayload,
            }),
          }),
        );
        cartResponse = await activeResponse.json();
              setCachedCart(cartResponse);
      }

      if (cartResponse.status || cartResponse.errors || !activeResponse.ok) {
        const noCartDrawer = true;
        const errorMessage = String(
          cartResponse.message ||
            cartResponse.description ||
            "Unable to update cart.",
        );

        this.dispatchEvent(
          new CartErrorEvent(
            form.getAttribute("id") || "",
            errorMessage,
            cartResponse.description,
            cartResponse.errors,
          ),
        );

        if (!addToCartTextError) return;
        addToCartTextError.classList.remove("hidden");

        // Reuse the text node if the user is spam-clicking
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          const newTextNode = document.createTextNode(errorMessage);
          addToCartTextError.appendChild(newTextNode);
        }

        // Create or get existing error live region for screen readers
        this.#setLiveRegionText(errorMessage);

        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add("hidden");

          // Clear the announcement
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);

        // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
        // because our back-end still adds the max allowed amount to the cart.
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: "product-form-component",
            itemCount: submittedQuantity,
            productId: this.dataset.productId,
            sellingPlanId: selectedSellingPlanId || "",
            noCartDrawer,
          }),
        );

        return;
      }

      const id = submittedVariantId;

      if (addToCartTextError) {
        addToCartTextError.classList.add("hidden");
        addToCartTextError.removeAttribute("aria-live");
      }

      if (!id) throw new Error("Form ID is required");

      if (
        selectedSellingPlanId &&
        (resolvedVoucherTag || resolvedVoucherName) &&
        subscriptionVoucherDiscountCodes.length
      ) {
        void persistVoucherCartAttribute(
          resolvedVoucherTag,
          resolvedVoucherName,
          {
            discountCodes: subscriptionVoucherDiscountCodes,
            sectionsPayload,
            force: true,
            returnResponse: true,
          },
        )
          .then((discountUpdateResponse) => {
            if (
              !discountUpdateResponse ||
              typeof discountUpdateResponse !== "object"
            ) {
              return;
            }

            document.dispatchEvent(
              new DiscountUpdateEvent(discountUpdateResponse, this.id),
            );
          })
          .catch((error) => {
            console.error("Failed to apply voucher discount codes.", error);
          });
      }

      // Add aria-live region to inform screen readers that the item was added
      // Get the added text from any add-to-cart button
      const anyAddToCartButton =
        allAddToCartContainers[0]?.refs?.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector(
          ".add-to-cart-text--added",
        );
        const addedText =
          addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: "product-form-component",
          itemCount: submittedQuantity,
          productId: this.dataset.productId,
          sellingPlanId: selectedSellingPlanId || "",
          sections: cartResponse.sections,
          noCartDrawer: this.dataset.noCartDrawer === "true",
        }),
      );

      if (shouldDeferLocalCartQuantityRefresh) {
        return;
      }

      // Keep local quantity labels accurate without delaying cart feedback.
      void this.#fetchAndUpdateCartQuantity();
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      setLoadingState(false);
      cartPerformance.measureFromEvent("add:user-action", event);
    });
}
  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle("hidden", cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null,
  ) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        "beforebegin",
        /** @type {Element} */ (newElement.cloneNode(true)),
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    const isProductCardForm = Boolean(this.closest("product-card"));

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? "";
    if (!isProductCardForm) {
      const currentSellingPlanContainer = this.querySelector(
        "[data-product-selling-plan-container]",
      );
      const newSellingPlanContainer = event.detail.data.html.querySelector(
        "[data-product-selling-plan-container]",
      );
      const currentPdpSubscriptionWidget = this.querySelector(
        "[data-pdp-subscription-widget]",
      );
      const newPdpSubscriptionWidget = event.detail.data.html.querySelector(
        "[data-pdp-subscription-widget]",
      );
      const currentSellingPlanValue = this.#getSellingPlanSelect()?.value || "";

      this.#morphOrUpdateElement(
        currentSellingPlanContainer,
        newSellingPlanContainer,
        this.refs.productFormButtons,
      );
      this.#morphOrUpdateElement(
        currentPdpSubscriptionWidget,
        newPdpSubscriptionWidget,
        this.refs.productFormButtons,
      );

      const updatedSellingPlanSelect = this.#getSellingPlanSelect();
      if (
        currentSellingPlanValue &&
        updatedSellingPlanSelect &&
        Array.from(updatedSellingPlanSelect.options).some(
          (option) => option.value === currentSellingPlanValue,
        )
      ) {
        updatedSellingPlanSelect.value = currentSellingPlanValue;
      }
    }

    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton =
      currentAddToCartButtonContainer?.refs?.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    if (this.refs.quantitySelector?.setDisabled) {
      this.refs.quantitySelector.setDisabled(
        event.detail.resource == null ||
          event.detail.resource.available == false,
      );
    }

    const newAddToCartButton = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]',
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available == false
      ) {
        acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector(
        'quantity-selector-component input[ref="quantityInput"]',
      )
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step,
      );
    }

    const newQuantityRules =
      event.detail.data.html.querySelector(".quantity-rules");
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem =
      event.detail.data.html.querySelector("price-per-item");
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if (
      (isQuantityRulesChanging || isPricePerItemChanging) &&
      quantitySelector
    ) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector(
        ".product-form-buttons",
      );

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]',
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        [".quantity-label", quantityLabel, quantitySelector],
        [".quantity-rules", quantityRules, this.refs.productFormButtons],
        ["price-per-item", pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(selector),
          fallback,
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing =
      event.detail.data.html.querySelector("volume-pricing");
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons,
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (isProductCardForm) {
      window.requestAnimationFrame(() => {
        void this.#syncSellingPlanLock();
        syncPdpSubscriptionWidget(this, {
          variant: event.detail.resource,
        });
      });
    } else {
      await this.#syncSellingPlanLock();
      syncPdpSubscriptionWidget(this, {
        variant: event.detail.resource,
      });
    }

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs?.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
if (typeof window !== "undefined") {
  initializeVoucherContext();
  getCachedCart(); // Pre-fetch cart on page load
}